<template>
  <div class="ai-voucher-generator">
    <h-card>
      <h2 slot="title">🧠 科目AI增强 - 智能凭证生成</h2>
      <p slot="title" class="subtitle">基于票据和银行回单，通过大模型智能生成会计凭证</p>

      <!-- 凭证期间选择 -->
      <div class="period-selector">
        <h4>📅 凭证期间</h4>
        <div class="period-controls">
          <div class="period-input">
            <label>年份:</label>
            <select v-model="voucherPeriod.year" class="year-select">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}年</option>
            </select>
          </div>
          <div class="period-input">
            <label>月份:</label>
            <select v-model="voucherPeriod.month" class="month-select">
              <option v-for="month in 12" :key="month" :value="month">第{{ String(month).padStart(2, '0') }}期</option>
            </select>
          </div>
          <div class="period-display">
            <span class="current-period">当前期间: {{ formatPeriod(voucherPeriod.year, voucherPeriod.month) }}</span>
          </div>
        </div>
      </div>

      <!-- 功能选择 -->
      <div class="function-selector">
        <h3 class="selector-title">选择凭证生成模式</h3>
        <div class="radio-cards">
          <div class="radio-card" :class="{active: selectedFunction === 'bill-based'}" @click="selectedFunction = 'bill-based'; onFunctionChange()">
            <div class="card-icon">📄</div>
            <div class="card-title">基于票据生成</div>
            <div class="card-desc">基于单张票据或票据组，AI智能生成对应的会计凭证</div>
          </div>
          <div class="radio-card" :class="{active: selectedFunction === 'receipt-based'}" @click="selectedFunction = 'receipt-based'; onFunctionChange()">
            <div class="card-icon">🏦</div>
            <div class="card-title">基于回单生成</div>
            <div class="card-desc">基于单张银行回单或回单组，AI智能生成对应的会计凭证</div>
          </div>
        </div>
      </div>

      <!-- 基于票据生成 -->
      <div v-if="selectedFunction === 'bill-based'" class="voucher-section">
        <h3>📄 基于票据的凭证生成</h3>
        <p class="section-desc">AI将分析票据的内容、金额、业务性质等信息，自动生成符合会计准则的凭证</p>

        <!-- 票据处理模式选择 -->
        <div class="sub-mode-selector">
          <h4>票据处理模式</h4>
          <div class="mode-tabs">
            <div class="mode-tab" :class="{active: billProcessMode === 'single'}" @click="billProcessMode = 'single'">
              <span class="tab-icon">📄</span>
              <span class="tab-text">单张票据</span>
            </div>
            <div class="mode-tab" :class="{active: billProcessMode === 'group'}" @click="billProcessMode = 'group'">
              <span class="tab-icon">📋</span>
              <span class="tab-text">票据组</span>
            </div>
          </div>
        </div>

        <div class="ai-config">
          <h4>AI生成配置</h4>
          <div class="config-row">
            <label>置信度阈值:</label>
            <div class="slider-container">
              <h-slider
                v-model="billConfig.confidenceThreshold"
                :min="0"
                :max="100"
                :step="10"
                style="width: 200px;">
              </h-slider>
              <div class="slider-marks">
                <span class="mark">0</span>
                <span class="mark">50</span>
                <span class="mark">100</span>
              </div>
            </div>
            <span class="threshold-value">{{ formatThreshold(billConfig.confidenceThreshold) }}</span>
            <span class="threshold-desc">({{ getThresholdDescription(billConfig.confidenceThreshold) }})</span>
          </div>
          <div class="config-row">
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.analyzeContent" class="config-checkbox">
              分析票据内容
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.analyzeAmount" class="config-checkbox">
              分析金额匹配
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.analyzeDate" class="config-checkbox">
              分析日期关联
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.analyzeCounterparty" class="config-checkbox">
              分析交易方
            </label>
          </div>
          <div class="config-row" v-if="billProcessMode === 'group'">
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.autoGrouping" class="config-checkbox">
              自动分组处理
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.mergeEntries" class="config-checkbox">
              合并相同科目
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="billConfig.validateBalance" class="config-checkbox">
              验证借贷平衡
            </label>
          </div>
        </div>

        <div class="data-selection">
          <!-- 单张模式 -->
          <div v-if="billProcessMode === 'single'">
            <h4>选择票据 ({{ filteredBills.length }}张) - 单张模式</h4>
            <div class="selection-hint">
              <p>💡 单张模式：每张票据将单独生成一张凭证</p>
            </div>

            <!-- 票据搜索筛选区域 -->
            <div class="search-filters">
              <div class="filter-row">
                <div class="filter-item">
                  <label>票据编号:</label>
                  <input v-model="billFilters.billNo" type="text" placeholder="请输入票据编号" @input="filterBills" />
                </div>
                <div class="filter-item">
                  <label>金额范围:</label>
                  <input v-model="billFilters.amountMin" type="number" placeholder="最小金额" @input="filterBills" />
                  <span class="range-separator">-</span>
                  <input v-model="billFilters.amountMax" type="number" placeholder="最大金额" @input="filterBills" />
                </div>
                <div class="filter-item">
                  <label>开票方:</label>
                  <input v-model="billFilters.issuer" type="text" placeholder="请输入开票方" @input="filterBills" />
                </div>
              </div>
              <div class="filter-row">
                <div class="filter-item">
                  <label>收票方:</label>
                  <input v-model="billFilters.recipient" type="text" placeholder="请输入收票方" @input="filterBills" />
                </div>
                <div class="filter-item">
                  <label>摘要:</label>
                  <input v-model="billFilters.summary" type="text" placeholder="请输入摘要关键词" @input="filterBills" />
                </div>
                <div class="filter-item">
                  <label>票据类型:</label>
                  <select v-model="billFilters.billType" @change="filterBills">
                    <option value="">全部类型</option>
                    <option value="增值税专用发票">增值税专用发票</option>
                    <option value="增值税普通发票">增值税普通发票</option>
                    <option value="收据">收据</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div class="filter-actions">
                  <button class="filter-btn reset-btn" @click="resetBillFilters">重置</button>
                  <button class="filter-btn search-btn" @click="filterBills">筛选</button>
                </div>
              </div>
            </div>

              <div class="bill-list">
              <div v-for="bill in filteredBills" :key="bill.id" class="bill-item"
                   :class="{selected: selectedBills.includes(bill.id), disabled: bill._processed}">
                <div v-if="bill._processed" class="corner-ribbon ribbon-processed">已生成凭证</div>
                <div class="bill-checkbox-wrapper">
                  <input type="checkbox"
                         :checked="selectedBills.includes(bill.id)"
                          :disabled="bill._processed"
                          @click.stop="toggleBillSelection(bill.id)"
                         class="bill-checkbox">
                </div>
                <div class="bill-info">
                  <div class="bill-header">
                    <span class="bill-no">{{ bill.billNo }}</span>
                    <span class="bill-amount">¥{{ formatAmount(bill.amount) }}</span>
                    <h-tag v-if="bill._processed" type="danger" size="small">已生成</h-tag>
                  </div>
                  <div class="bill-details">
                    <span class="bill-date">{{ bill.billDate }}</span>
                    <span class="bill-type">{{ bill.type }}</span>
                    <span class="bill-issuer">{{ bill.issuer }}</span>
                  </div>
                  <div class="bill-summary">{{ bill.summary || '无摘要' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 票据组模式 -->
          <div v-if="billProcessMode === 'group'">
            <h4>选择票据归并组 ({{ billGroups.length }}组) - 票据组模式</h4>
            <div class="selection-hint">
              <p>💡 票据组模式：选择票据归并组，AI将基于组内的多张票据生成关联凭证</p>
            </div>

            <div class="group-list">
              <div v-for="group in billGroups" :key="group.groupId" class="group-item"
                   :class="{selected: selectedBills.includes(group.groupId), disabled: group._processed}">
                <div v-if="group._processed" class="corner-ribbon ribbon-processed">已生成凭证</div>
                <div class="group-checkbox-wrapper">
                  <input type="checkbox"
                          :checked="selectedBills.includes(group.groupId)"
                          :disabled="group._processed"
                          @click.stop="toggleBillSelection(group.groupId)"
                         class="group-checkbox">
                </div>
                <div class="group-info">
                  <div class="group-header">
                    <span class="group-name">{{ group.groupName }}</span>
                    <span class="group-amount">¥{{ formatAmount(group.totalAmount) }}</span>
                  </div>
                  <div class="group-details">
                    <span class="group-count">{{ group.itemCount }}张票据</span>
                    <span class="group-date">{{ group.createdAt }}</span>
                    <span class="group-status">{{ group.status === 'ACTIVE' ? '活跃' : '已解散' }}</span>
                    <span class="group-processed" v-if="group._processed">已生成凭证</span>
                  </div>
                  <div class="group-summary">{{ group.groupSummary || '无摘要' }}</div>

                  <!-- 组内票据预览 -->
                  <div class="group-items-preview" v-if="group.documents && group.documents.length > 0">
                    <div class="preview-title">组内票据:</div>
                    <div class="preview-items">
                      <span v-for="doc in group.documents.slice(0, 3)" :key="doc.id" class="preview-item">
                        {{ doc.billNo }}(¥{{ formatAmount(doc.amount) }})
                      </span>
                      <span v-if="group.documents.length > 3" class="preview-more">
                        等{{ group.documents.length }}张
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="data-stats">
            <span class="stat-item">已选择: {{ selectedBills.length }}张</span>
            <span class="stat-item">总金额: ¥{{ selectedBillsAmount.toLocaleString() }}</span>
            <span class="stat-item" v-if="billProcessMode === 'single'">预计生成: {{ selectedBills.length }}张凭证</span>
            <span class="stat-item" v-if="billProcessMode === 'group' && selectedBills.length > 0">预计生成: 1-{{ Math.ceil(selectedBills.length/3) }}张凭证</span>
          </div>
          <h-button type="primary" @click="startBillGeneration" :loading="aiProcessing" :disabled="selectedBills.length === 0">
            🤖 开始AI智能凭证生成
          </h-button>
        </div>
      </div>

      <!-- 基于回单生成 -->
      <div v-if="selectedFunction === 'receipt-based'" class="voucher-section">
        <h3>🏦 基于回单的凭证生成</h3>
        <p class="section-desc">AI将分析银行回单的交易信息、资金流向等，自动生成符合会计准则的凭证</p>

        <!-- 回单处理模式选择 -->
        <div class="sub-mode-selector">
          <h4>回单处理模式</h4>
          <div class="mode-tabs">
            <div class="mode-tab" :class="{active: receiptProcessMode === 'single'}" @click="receiptProcessMode = 'single'">
              <span class="tab-icon">🏦</span>
              <span class="tab-text">单张回单</span>
            </div>
            <div class="mode-tab" :class="{active: receiptProcessMode === 'group'}" @click="receiptProcessMode = 'group'">
              <span class="tab-icon">📊</span>
              <span class="tab-text">回单组</span>
            </div>
          </div>
        </div>

        <div class="ai-config">
          <h4>AI生成配置</h4>
          <div class="config-row">
            <label>置信度阈值:</label>
            <div class="slider-container">
              <h-slider
                v-model="receiptConfig.confidenceThreshold"
                :min="0"
                :max="100"
                :step="10"
                style="width: 200px;">
              </h-slider>
              <div class="slider-marks">
                <span class="mark">0</span>
                <span class="mark">50</span>
                <span class="mark">100</span>
              </div>
            </div>
            <span class="threshold-value">{{ formatThreshold(receiptConfig.confidenceThreshold) }}</span>
            <span class="threshold-desc">({{ getThresholdDescription(receiptConfig.confidenceThreshold) }})</span>
          </div>
          <div class="config-row">
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.analyzeTransaction" class="config-checkbox">
              分析交易性质
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.analyzeCounterparty" class="config-checkbox">
              分析交易对方
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.analyzeFlow" class="config-checkbox">
              分析资金流向
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.analyzeAmount" class="config-checkbox">
              分析金额匹配
            </label>
          </div>
          <div class="config-row" v-if="receiptProcessMode === 'group'">
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.autoGrouping" class="config-checkbox">
              自动分组处理
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.mergeEntries" class="config-checkbox">
              合并相同科目
            </label>
            <label class="checkbox-label">
              <input type="checkbox" v-model="receiptConfig.validateBalance" class="config-checkbox">
              验证借贷平衡
            </label>
          </div>
        </div>

        <div class="data-selection">
          <!-- 单张模式 -->
          <div v-if="receiptProcessMode === 'single'">
            <div class="receipt-header">
              <h4>选择银行回单 - 单张模式</h4>
              <div class="data-summary">
                <span class="total-info">
                  总计: {{ receiptPagination.total }}张 | 第{{ receiptPagination.current }}/{{ receiptPagination.pages }}页
                </span>
                <span class="amount-info">
                  当前页金额: ¥{{ currentPageReceiptAmount.toLocaleString() }}
                </span>
              </div>
            </div>
            <div class="selection-hint">
              <p>💡 单张模式：每张回单将单独生成一张凭证</p>
            </div>

            <!-- 分页控制 -->
            <div class="pagination-controls" v-if="receiptPagination.total > receiptPagination.size">
              <div class="pagination-info">
                显示第 {{ (receiptPagination.current - 1) * receiptPagination.size + 1 }} - 
                {{ Math.min(receiptPagination.current * receiptPagination.size, receiptPagination.total) }} 条，
                共 {{ receiptPagination.total }} 条记录
              </div>
              <div class="pagination-buttons">
                <button 
                  class="pagination-btn" 
                  :disabled="receiptPagination.current <= 1"
                  @click="goToReceiptPage(receiptPagination.current - 1)">
                  上一页
                </button>
                
                <div class="page-numbers">
                  <template v-for="(page, index) in getVisiblePages(receiptPagination.current, receiptPagination.pages)">
                    <span v-if="page === '...'" :key="'ellipsis-' + index" class="page-ellipsis">...</span>
                    <button 
                      v-else
                      :key="'page-' + page"
                      class="page-btn"
                      :class="{ active: page === receiptPagination.current }"
                      @click="goToReceiptPage(page)">
                      {{ page }}
                    </button>
                  </template>
                </div>
                
                <button 
                  class="pagination-btn" 
                  :disabled="receiptPagination.current >= receiptPagination.pages"
                  @click="goToReceiptPage(receiptPagination.current + 1)">
                  下一页
                </button>
              </div>
            </div>

            <!-- 搜索筛选区域 -->
            <div class="search-filters">
              <div class="filter-row">
                <div class="filter-item">
                  <label>回单编号:</label>
                  <input v-model="receiptFilters.receiptsNo" type="text" placeholder="请输入回单编号" @input="filterReceipts" />
                </div>
                <div class="filter-item">
                  <label>金额范围:</label>
                  <input v-model="receiptFilters.amountMin" type="number" placeholder="最小金额" @input="filterReceipts" />
                  <span class="range-separator">-</span>
                  <input v-model="receiptFilters.amountMax" type="number" placeholder="最大金额" @input="filterReceipts" />
                </div>
                <div class="filter-item">
                  <label>交易对手:</label>
                  <input v-model="receiptFilters.counterparty" type="text" placeholder="请输入交易对手" @input="filterReceipts" />
                </div>
              </div>
              <div class="filter-row">
                <div class="filter-item">
                  <label>摘要:</label>
                  <input v-model="receiptFilters.summary" type="text" placeholder="请输入摘要关键词" @input="filterReceipts" />
                </div>
                <div class="filter-item">
                  <label>支付方式:</label>
                  <select v-model="receiptFilters.paymentMethod" @change="filterReceipts">
                    <option value="">全部方式</option>
                    <option value="转账">转账</option>
                    <option value="现金">现金</option>
                    <option value="支票">支票</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div class="filter-actions">
                  <button class="filter-btn reset-btn" @click="resetReceiptFilters">重置</button>
                  <button class="filter-btn search-btn" @click="filterReceipts">筛选</button>
                </div>
              </div>
            </div>

            <div class="receipt-list">
              <div v-for="receipt in filteredReceipts" :key="receipt.id" class="receipt-item"
                   :class="{selected: selectedReceipts.includes(receipt.id), disabled: receipt._processed}">
                <div v-if="receipt._processed" class="corner-ribbon ribbon-processed">已生成凭证</div>
                <div class="receipt-checkbox-wrapper">
                  <input type="checkbox"
                         :checked="selectedReceipts.includes(receipt.id)"
                          :disabled="receipt._processed"
                          @click.stop="toggleReceiptSelection(receipt.id)"
                         class="receipt-checkbox">
                </div>
                <div class="receipt-info">
                  <div class="receipt-header">
                    <span class="receipt-no">{{ receipt.receiptsNo }}</span>
                    <span class="receipt-amount">¥{{ formatAmount(receipt.amount) }}</span>
                    <h-tag v-if="receipt._processed" type="danger" size="small">已生成</h-tag>
                  </div>
                  <div class="receipt-details">
                    <span class="receipt-date">{{ receipt.receiptsDate }}</span>
                    <span class="receipt-type">{{ receipt.paymentMethod || '转账' }}</span>
                    <span class="receipt-counterparty">{{ getCounterparty(receipt) }}</span>
                  </div>
                  <div class="receipt-extended-info">
                    <div class="info-row">
                      <span class="info-label">收款人:</span>
                      <span class="info-value">{{ receipt.payeeName || '未知' }}</span>
                      <span class="info-label">付款人:</span>
                      <span class="info-value">{{ receipt.payerName || '未知' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">收款行:</span>
                      <span class="info-value">{{ receipt.payeeBank || '未知银行' }}</span>
                      <span class="info-label">收款账号:</span>
                      <span class="info-value">{{ receipt.payeeAccount || '未知账号' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">付款行:</span>
                      <span class="info-value">{{ receipt.payerBank || '未知银行' }}</span>
                      <span class="info-label">付款账号:</span>
                      <span class="info-value">{{ receipt.payerAccount || '未知账号' }}</span>
                    </div>
                  </div>
                  <div class="receipt-summary">{{ receipt.summary || '无摘要' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 回单组模式 -->
          <div v-if="receiptProcessMode === 'group'">
            <h4>选择银行回单归并组 ({{ receiptGroups.length }}组) - 回单组模式</h4>
            <div class="selection-hint">
              <p>💡 回单组模式：选择银行回单归并组，AI将基于组内的多张回单生成关联凭证</p>
            </div>

            <div class="group-list">
              <div v-for="group in receiptGroups" :key="group.groupId" class="group-item"
                   :class="{selected: selectedReceipts.includes(group.groupId)}">
                <div class="group-checkbox-wrapper">
                  <input type="checkbox"
                         :checked="selectedReceipts.includes(group.groupId)"
                         @click.stop="toggleReceiptSelection(group.groupId)"
                         class="group-checkbox">
                </div>
                <div class="group-info">
                  <div class="group-header">
                    <span class="group-name">{{ group.groupName }}</span>
                    <span class="group-amount">¥{{ formatAmount(group.totalAmount) }}</span>
                  </div>
                  <div class="group-details">
                    <span class="group-count">{{ group.itemCount }}张回单</span>
                    <span class="group-date">{{ group.createdAt }}</span>
                    <span class="group-status">{{ group.status === 'ACTIVE' ? '活跃' : '已解散' }}</span>
                  </div>
                  <div class="group-summary">{{ group.groupSummary || '无摘要' }}</div>

                  <!-- 组内回单预览 -->
                  <div class="group-items-preview" v-if="group.receipts && group.receipts.length > 0">
                    <div class="preview-title">组内回单:</div>
                    <div class="preview-items">
                      <span v-for="receipt in group.receipts.slice(0, 3)" :key="receipt.id" class="preview-item">
                        {{ receipt.receiptsNo }}(¥{{ formatAmount(receipt.amount) }})
                      </span>
                      <span v-if="group.receipts.length > 3" class="preview-more">
                        等{{ group.receipts.length }}张
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="data-stats">
            <span class="stat-item">已选择: {{ selectedReceipts.length }}张</span>
            <span class="stat-item">总金额: ¥{{ selectedReceiptsAmount.toLocaleString() }}</span>
            <span class="stat-item" v-if="receiptProcessMode === 'single'">预计生成: {{ selectedReceipts.length }}张凭证</span>
            <span class="stat-item" v-if="receiptProcessMode === 'group' && selectedReceipts.length > 0">预计生成: 1-{{ Math.ceil(selectedReceipts.length/3) }}张凭证</span>
          </div>
          <h-button type="primary" @click="startReceiptGeneration" :loading="aiProcessing" :disabled="selectedReceipts.length === 0">
            🤖 开始AI智能凭证生成
          </h-button>
        </div>
      </div>



      <!-- AI生成结果 -->
      <div v-if="generatedVouchers.length > 0" class="ai-results">
        <h3>🎯 AI生成结果</h3>
        <div class="results-summary">
          <span class="result-stat">生成 {{ generatedVouchers.length }} 张凭证</span>
          <span class="result-stat">平均置信度: {{ averageConfidence }}%</span>
          <span class="result-stat">预计节省 {{ estimatedTimeSaving }} 分钟</span>
          <span v-if="presetInfo" class="preset-tip">已根据预选加载：{{ presetInfo }}</span>
          <span v-if="presetInfo" class="clear-preset" @click="clearPresetSelection">清除预选</span>
        </div>

        <div class="results-list">
          <div v-for="(voucher, index) in generatedVouchers" :key="index" class="result-item">
            <div class="result-header">
              <h4>凭证 #{{ index + 1 }}</h4>
              <div class="confidence-badge" :class="getConfidenceClass(voucher.confidence)">
                置信度: {{ voucher.confidence }}%
              </div>
            </div>

            <div class="voucher-content">
              <div class="voucher-info">
                <div class="info-row">
                  <span class="label">凭证字号:</span>
                  <span class="value">{{ voucher.word || voucher.voucherWord }}-{{ voucher.code || voucher.voucherNumber }}</span>
                </div>
                <div class="info-row">
                  <span class="label">凭证日期:</span>
                  <span class="value">{{ voucher.voucherDate }}</span>
                </div>
                <div class="info-row">
                  <span class="label">附件张数:</span>
                  <span class="value">{{ voucher.attachmentCount }}张</span>
                </div>
              </div>

                  <!-- VAT 专票提示条 -->
                  <div v-if="isVatSpecialVoucher(voucher)" class="vat-banner">
                    ⚠️ 检测到关联的增值税专用发票：
                    <span v-if="vatMeta(voucher).taxRate != null">税率：{{ vatMeta(voucher).taxRate }}</span>
                    <span v-if="vatMeta(voucher).taxAmount != null">，合计税额：{{ formatAmount(vatMeta(voucher).taxAmount) }}</span>
                    <span>（建议使用“🧮 一键补进项税额”快速补齐）</span>
                  </div>


              <div class="voucher-entries">
                <div class="voucher-header-info">
                  <span class="voucher-period">期间: {{ voucher.voucherPeriod || formatPeriod(voucherPeriod.year, voucherPeriod.month) }}</span>
                  <span class="voucher-date">日期: {{ voucher.voucherDate }}</span>
                  <span class="voucher-number">凭证字号: {{ voucher.word || voucher.voucherWord }}-{{ voucher.code || voucher.voucherNumber }}</span>
                </div>

                <table class="entries-table">
                  <thead>
                    <tr>
                      <th width="60">摘要</th>
                      <th width="200">科目</th>
                      <th width="100">借方金额</th>
                      <th width="100">贷方金额</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(entry, entryIndex) in (voucher.details || voucher.entries)" :key="entryIndex">
                      <td>{{ entry.summary }}</td>
                      <td>{{ entry.subjectCode }}-{{ entry.subjectName }}</td>
                      <td class="amount">{{ entry.debitAmount ? formatAmount(entry.debitAmount) : '' }}</td>
                      <td class="amount">{{ entry.creditAmount ? formatAmount(entry.creditAmount) : '' }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="total-row">
                      <td colspan="2">合计</td>
                      <td class="amount">{{ formatAmount(voucher.totalDebit || voucher.totalAmount) }}</td>
                      <td class="amount">{{ formatAmount(voucher.totalCredit || voucher.totalAmount) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div class="ai-analysis">
                <h5>AI分析说明</h5>
                <p>{{ voucher.aiReasoning || voucher.aiAnalysis }}</p>
              </div>
            </div>

            <div class="result-actions">
              <h-button type="success" size="small" @click="confirmVoucher(voucher)">
                ✅ 确认生成
              </h-button>
              <h-button type="warning" size="small" @click="editVoucher(voucher)">
                ✏️ 编辑修改
              </h-button>
              <h-button type="primary" size="small" @click="oneClickAddInputTax(voucher)">
                🧮 一键补进项税额
              </h-button>
              <h-button type="danger" size="small" @click="rejectVoucher(voucher)">
                ❌ 拒绝建议
              </h-button>
            </div>
          </div>
        </div>

        <!-- 批量操作 -->
        <div class="batch-operations">
          <h-button type="primary" @click="confirmAllVouchers" :loading="batchProcessing">
            ✅ 批量确认所有凭证
          </h-button>
          <h-button type="warning" @click="confirmHighConfidenceVouchers" :loading="batchProcessing">
            🎯 仅确认高置信度凭证
          </h-button>
        </div>
      </div>
    </h-card>

    <!-- 凭证编辑模态框 -->
    <h-modal v-model="editModalVisible" title="编辑AI生成的凭证" width="1200" :mask-closable="false">
      <div class="voucher-edit-form" v-if="editingVoucher">
        <div class="form-header">
          <div style="display: flex; align-items: center; gap: 20px; margin-bottom: 20px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <Select
                v-model="editForm.word"
                :datas="voucherWords"
                keyName="word"
                titleName="word"
                style="min-width: 70px"
                :deletable="false"
                placeholder="记"
              />
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <NumberInput
                :min="1"
                v-model="editForm.code"
                v-width="90"
                style="display: inline-block"
              />
              <span>号</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <span>日期：</span>
              <DatePicker
                :clearable="false"
                v-model="editForm.voucherDate"
                format="YYYY-MM-DD"
              />
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <span>备注：</span>
              <input
                type="text"
                v-model="editForm.remark"
                placeholder="请输入备注内容"
                style="width: 200px; padding: 6px 12px; border: 1px solid #ddd; border-radius: 4px;"
              />
            </div>
          </div>
        </div>

        <!-- 使用VoucherTable组件 -->
        <voucher-table ref="editVoucherTable" v-model="editVoucherTable"/>

        <div class="ai-analysis-section" style="margin-top: 20px;">
          <h4 style="margin-bottom: 10px; color: #333;">AI分析说明</h4>
          <div class="ai-analysis-content" style="padding: 12px; background: #f8f9fa; border-radius: 6px; border: 1px solid #e9ecef; color: #666; line-height: 1.5;">
            {{ editingVoucher.aiReasoning || editingVoucher.aiAnalysis || '暂无AI分析说明' }}
          </div>
        </div>
      </div>

      <div slot="footer">
        <h-button @click="editModalVisible = false">取消</h-button>
        <h-button
          v-if="isVatSpecialVoucher(editingVoucher)"
          color="warning"
          @click="oneClickAddInputTaxInEdit"
          style="margin-right: 8px;"
        >
          一键补进项税额
        </h-button>
        <h-button color="primary" @click="saveEditedVoucher" :loading="saving">保存凭证</h-button>
      </div>
    </h-modal>

    <!-- 反馈收集（原生覆盖层，避免UI库问题） -->
    <div v-if="feedbackModalVisible && useNativeFeedbackModal" class="feedback-overlay">
      <div class="feedback-dialog">
        <div class="dialog-header">提交AI生成反馈</div>
        <div class="dialog-body">
          <div class="form-row" v-if="feedbackMode !== 'reject'">
            <label>是否接受本次AI结果：</label>
            <label class="checkbox-inline">
              <input type="checkbox" v-model="feedbackForm.accepted" /> 接受
            </label>
          </div>
          <div class="form-row" v-else>
            <label>本次操作：</label>
            <span style="color:#ff4d4f;font-weight:600;">拒绝该凭证建议</span>
          </div>
          <div class="form-row">
            <label>原因标签：</label>
            <input v-model="feedbackForm.reason" type="text" placeholder="如：科目不匹配、金额拆分有误、摘要不准确" class="input" />
          </div>
          <div class="form-row">
            <label>自定义标签：</label>
            <input v-model="feedbackForm.tags" type="text" placeholder="多标签以逗号分隔" class="input" />
          </div>
          <div class="form-row">
            <label>差异JSON（可选）：</label>
            <textarea v-model="feedbackForm.diffJson" rows="8" class="textarea" placeholder='{"entriesChanged": [...]}'></textarea>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn" @click="feedbackModalVisible = false">取消</button>
          <button class="btn primary" :disabled="feedbackSubmitting" @click="submitVoucherFeedback">{{ feedbackForm.accepted ? '提交反馈' : '确认拒绝并提交' }}</button>
        </div>
      </div>
    </div>

    <!-- 反馈收集模态框（HeyUI，作为后备） -->
    <h-modal v-else v-model="feedbackModalVisible" title="提交AI生成反馈" width="700" :mask-closable="false">
      <div class="feedback-form">
        <div class="form-row" v-if="feedbackMode !== 'reject'">
          <label>是否接受本次AI结果：</label>
          <h-switch v-model="feedbackForm.accepted" on-text="接受" off-text="不接受" />
        </div>
        <div class="form-row" v-else>
          <label>本次操作：</label>
          <h-tag type="danger">拒绝该凭证建议</h-tag>
        </div>
        <div class="form-row">
          <label>原因标签：</label>
          <input v-model="feedbackForm.reason" type="text" placeholder="如：科目不匹配、金额拆分有误、摘要不准确" class="input" />
        </div>
        <div class="form-row">
          <label>自定义标签：</label>
          <input v-model="feedbackForm.tags" type="text" placeholder="多标签以逗号分隔" class="input" />
        </div>
        <div class="form-row">
          <label>差异JSON（可选）：</label>
          <textarea v-model="feedbackForm.diffJson" rows="8" class="textarea" placeholder='{"entriesChanged": [...]}'></textarea>
        </div>
      </div>
      <div slot="footer">
        <h-button @click="feedbackModalVisible = false">取消</h-button>
        <h-button type="primary" :loading="feedbackSubmitting" @click="submitVoucherFeedback">
          {{ feedbackForm.accepted ? '提交反馈' : '确认拒绝并提交' }}
        </h-button>
      </div>
    </h-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import VoucherTable from "../../components/VoucherTable"

export default {
  name: 'AiVoucherGenerator',
  components: { VoucherTable },
  data() {
    return {
      selectedFunction: 'bill-based',
      aiProcessing: false,
      batchProcessing: false,

      // 处理模式
      billProcessMode: 'single',    // single: 单张票据, group: 票据组
      receiptProcessMode: 'single', // single: 单张回单, group: 回单组

      // 数据源
      availableBills: [],
      unrelatedReceipts: [],
      billGroups: [], // 票据归并组
      receiptGroups: [], // 银行回单归并组
      
      // 分页相关
      receiptPagination: {
        current: 1,    // 当前页码
        size: 10,      // 每页大小
        total: 0,      // 总记录数
        pages: 0       // 总页数
      },
      loadingReceipts: false, // 是否正在加载数据

      // 选择状态
      selectedBills: [],
      selectedReceipts: [],

      // 筛选参数
      receiptFilters: {
        receiptsNo: '',
        amountMin: '',
        amountMax: '',
        counterparty: '',
        summary: '',
        paymentMethod: ''
      },
      billFilters: {
        billNo: '',
        amountMin: '',
        amountMax: '',
        issuer: '',
        recipient: '',
        summary: '',
        billType: ''
      },

      // 凭证字数据
      voucherWords: [],

      // 编辑相关
      editVoucherTable: {voucherItems: []},

      // 配置
      billConfig: {
        confidenceThreshold: 80,
        analyzeContent: true,
        analyzeAmount: true,
        analyzeDate: false,
        analyzeCounterparty: true,
        autoGrouping: true,
        mergeEntries: true,
        validateBalance: true
      },
      receiptConfig: {
        confidenceThreshold: 85,
        analyzeTransaction: true,
        analyzeCounterparty: true,
        analyzeFlow: true,
        analyzeAmount: true,
        autoGrouping: true,
        mergeEntries: true,
        validateBalance: true
      },

      // 生成结果
      generatedVouchers: [],

      // 凭证期间 - 默认为空，在mounted中从账套获取
      voucherPeriod: {
        year: null,
        month: null
      },

      // 编辑模态框相关
      editModalVisible: false,
      editingVoucher: null,
      editForm: {
        word: '记',
        code: 1,
        voucherDate: '',
        remark: '',
        details: []
      },
      saving: false,
      voucherWords: [
        { word: '记', isDefault: true },
        { word: '收', isDefault: false },
        { word: '付', isDefault: false },
        { word: '转', isDefault: false }
      ],

      // 末级科目校验缓存
      leafSubjects: { list: [], loaded: false, loading: false },

      // 反馈相关
      feedbackModalVisible: false,
      feedbackSubmitting: false,
      feedbackForm: {
        historyId: null,
        accepted: true,
        reason: '',
        tags: '',
        diffJson: ''
      },
      feedbackContext: {
        originalVoucher: null,
        finalEntries: null
      },
      // 若heyui modal失效，回退到原生覆盖层
      useNativeFeedbackModal: false,
      feedbackMode: 'normal',
      presetInfo: ''
    }
  },
  created() {
    // URL参数预配置：mode=bill|receipt, ids=1,2,3
    const mode = this.$route && this.$route.query && this.$route.query.mode
    const idsParam = this.$route && this.$route.query && this.$route.query.ids
    if (mode === 'receipt') {
      this.selectedFunction = 'receipt-based'
    } else if (mode === 'bill') {
      this.selectedFunction = 'bill-based'
    }
    if (idsParam) {
      const ids = String(idsParam).split(',').map(x => x.trim()).filter(Boolean)
      // 懒加载后选中
      this.$nextTick(async () => {
        try {
          await Promise.all([this.loadAvailableBills(), this.loadAvailableReceipts()])
          if (mode === 'receipt') {
            this.selectedReceipts = ids
            this.presetInfo = `回单 ${ids.length} 条`
          } else {
            this.selectedBills = ids
            this.presetInfo = `票据 ${ids.length} 条`
          }
        } catch (e) {
          console.warn('预选数据加载失败', e)
        }
      })
    }
  },

  mounted() {
    console.log('AI凭证生成器组件已挂载，开始加载数据...')

    // 设置默认凭证期间为账套当前期间
    this.initVoucherPeriod()

    // 直接加载数据，不依赖用户状态检查（参考AI关联引擎）
    this.loadData()
    this.loadVoucherWords()
  },
  computed: {
    ...mapState(['User', 'currentAccountSets']),
    selectedBillsAmount() {
      return this.selectedBills.reduce((total, billId) => {
        const bill = this.availableBills.find(b => b.id === billId)
        return total + (bill ? bill.amount : 0)
      }, 0)
    },
    selectedReceiptsAmount() {
      return this.selectedReceipts.reduce((total, receiptId) => {
        const receipt = this.unrelatedReceipts.find(r => r.id === receiptId)
        return total + (receipt ? receipt.amount : 0)
      }, 0)
    },
    totalBillAmount() {
      return this.availableBills.reduce((total, bill) => total + bill.amount, 0)
    },
    totalReceiptAmount() {
      return this.unrelatedReceipts.reduce((total, receipt) => total + receipt.amount, 0)
    },
    currentPageReceiptAmount() {
      return this.filteredReceipts.reduce((total, receipt) => total + receipt.amount, 0)
    },
    averageConfidence() {
      if (this.generatedVouchers.length === 0) return 0
      const total = this.generatedVouchers.reduce((sum, v) => sum + v.confidence, 0)
      return Math.round(total / this.generatedVouchers.length)
    },
    estimatedTimeSaving() {
      return this.generatedVouchers.length * 15 // 每张凭证节省15分钟
    },
    availableYears() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = currentYear - 2; i <= currentYear + 1; i++) {
        years.push(i)
      }
      return years
    },

    // 过滤后的银行回单
    filteredReceipts() {
      let filtered = this.unrelatedReceipts || []

      // 回单编号筛选
      if (this.receiptFilters.receiptsNo) {
        const keyword = this.receiptFilters.receiptsNo.toLowerCase()
        filtered = filtered.filter(receipt =>
          receipt.receiptsNo && receipt.receiptsNo.toLowerCase().includes(keyword)
        )
      }

      // 金额范围筛选
      if (this.receiptFilters.amountMin) {
        const minAmount = parseFloat(this.receiptFilters.amountMin)
        filtered = filtered.filter(receipt =>
          receipt.amount >= minAmount
        )
      }
      if (this.receiptFilters.amountMax) {
        const maxAmount = parseFloat(this.receiptFilters.amountMax)
        filtered = filtered.filter(receipt =>
          receipt.amount <= maxAmount
        )
      }

      // 交易对手筛选
      if (this.receiptFilters.counterparty) {
        const keyword = this.receiptFilters.counterparty.toLowerCase()
        filtered = filtered.filter(receipt =>
          (receipt.payeeName && receipt.payeeName.toLowerCase().includes(keyword)) ||
          (receipt.payerName && receipt.payerName.toLowerCase().includes(keyword)) ||
          (receipt.payeeBank && receipt.payeeBank.toLowerCase().includes(keyword)) ||
          (receipt.payerBank && receipt.payerBank.toLowerCase().includes(keyword))
        )
      }

      // 摘要筛选
      if (this.receiptFilters.summary) {
        const keyword = this.receiptFilters.summary.toLowerCase()
        filtered = filtered.filter(receipt =>
          receipt.summary && receipt.summary.toLowerCase().includes(keyword)
        )
      }

      // 支付方式筛选
      if (this.receiptFilters.paymentMethod) {
        filtered = filtered.filter(receipt =>
          receipt.paymentMethod === this.receiptFilters.paymentMethod
        )
      }

      return filtered
    },

    // 过滤后的票据
    filteredBills() {
      let filtered = this.availableBills || []

      // 票据编号筛选
      if (this.billFilters.billNo) {
        const keyword = this.billFilters.billNo.toLowerCase()
        filtered = filtered.filter(bill =>
          bill.billNo && bill.billNo.toLowerCase().includes(keyword)
        )
      }

      // 金额范围筛选
      if (this.billFilters.amountMin) {
        const minAmount = parseFloat(this.billFilters.amountMin)
        filtered = filtered.filter(bill =>
          bill.amount >= minAmount
        )
      }
      if (this.billFilters.amountMax) {
        const maxAmount = parseFloat(this.billFilters.amountMax)
        filtered = filtered.filter(bill =>
          bill.amount <= maxAmount
        )
      }

      // 开票方筛选
      if (this.billFilters.issuer) {
        const keyword = this.billFilters.issuer.toLowerCase()
        filtered = filtered.filter(bill =>
          bill.issuer && bill.issuer.toLowerCase().includes(keyword)
        )
      }

      // 收票方筛选
      if (this.billFilters.recipient) {
        const keyword = this.billFilters.recipient.toLowerCase()
        filtered = filtered.filter(bill =>
          bill.recipient && bill.recipient.toLowerCase().includes(keyword)
        )
      }

      // 摘要筛选
      if (this.billFilters.summary) {
        const keyword = this.billFilters.summary.toLowerCase()
        filtered = filtered.filter(bill =>
          bill.summary && bill.summary.toLowerCase().includes(keyword)
        )
      }

      // 票据类型筛选
      if (this.billFilters.billType) {
        filtered = filtered.filter(bill =>
          bill.type === this.billFilters.billType || bill.billType === this.billFilters.billType
        )
      }

      return filtered
    }
  },
  watch: {
    // 监听账套变化，重新设置凭证期间
    currentAccountSets: {
      handler(newVal) {
        if (newVal && newVal.currentAccountDate) {
          console.log('检测到账套数据变化，重新设置凭证期间')
          this.initVoucherPeriod()
        }
      },
      immediate: true // 立即执行，包括初始值
    }
  },
  methods: {
    // 初始化凭证期间为账套当前期间
    initVoucherPeriod() {
      try {
        // 从账套获取当前期间
        if (this.currentAccountSets && this.currentAccountSets.currentAccountDate) {
          const accountDate = new Date(this.currentAccountSets.currentAccountDate)
          this.voucherPeriod.year = accountDate.getFullYear()
          this.voucherPeriod.month = accountDate.getMonth() + 1
          console.log('设置凭证期间为账套当前期间:', this.voucherPeriod.year, '年', this.voucherPeriod.month, '月')
          return true // 表示成功设置
        } else {
          console.log('账套数据尚未加载，当前状态:', {
            hasCurrentAccountSets: !!this.currentAccountSets,
            currentAccountDate: this.currentAccountSets && this.currentAccountSets.currentAccountDate
          })
          return false // 表示需要后续重试
        }
      } catch (error) {
        console.error('初始化凭证期间失败:', error)
        // 发生错误时使用当前日期
        const now = new Date()
        this.voucherPeriod.year = now.getFullYear()
        this.voucherPeriod.month = now.getMonth() + 1
        console.warn('发生错误，使用当前日期作为后备:', this.voucherPeriod.year, '年', this.voucherPeriod.month, '月')
        return true // 错误情况下也认为完成
      }
    },

    clearPresetSelection() {
      this.selectedBills = []
      this.selectedReceipts = []
      this.presetInfo = ''
      // 清除URL参数视觉干扰（保留路由路径）
      if (this.$route && (this.$route.query.mode || this.$route.query.ids)) {
        this.$router.replace({ path: this.$route.path })
      }
    },

    // 银行回单筛选方法
    filterReceipts() {
      // 筛选逻辑已在computed属性中实现
      console.log('筛选银行回单:', this.receiptFilters)
    },

    resetReceiptFilters() {
      this.receiptFilters = {
        receiptsNo: '',
        amountMin: '',
        amountMax: '',
        counterparty: '',
        summary: '',
        paymentMethod: ''
      }
      console.log('重置银行回单筛选条件')
    },

    async markProcessed(type, items, setter) {
      try {
        if (!items || items.length === 0) return
        const ids = items.map(it => it.id).filter(Boolean)
        if (!ids || ids.length === 0) return
        // 使用后端 /relations/processed 支持逗号分隔的ids，并统计任意方向与凭证的关联
        const resp = await fetch(`/api/relations/processed?type=${encodeURIComponent(type)}&ids=${encodeURIComponent(ids.join(','))}`, {
          credentials: 'include'
        })
        if (!resp.ok) return
        const data = await resp.json()
        if (data && data.success && data.data) {
          items.forEach(it => {
            const processed = !!data.data[String(it.id)]
            setter && setter(it, processed)
          })
        }
      } catch (e) {
        // 静默失败，不阻断流程
      }
    },


    // VAT 专票识别

    // 统一抽取 VAT 专票相关信息（兼容 sourceBill/sourceReceipt/sourceItems 三种来源）
    vatInfo(voucher) {
      const info = { isVatSpecial: false, taxRate: null, taxAmount: null, totalAmount: null }
      const b = voucher && voucher.sourceBill
      const r = voucher && voucher.sourceReceipt
      console.log('vatInfo检测 - sourceBill:', b)
      console.log('vatInfo检测 - sourceReceipt:', r)
      // 1) 直接来源
      if (b && (b.type === '增值税专用发票' || b.type === '电子发票(专用发票)' || b.billType === '增值税专用发票' || b.billType === '电子发票(专用发票)')) {
        info.isVatSpecial = true
        if (b.taxRate != null) info.taxRate = b.taxRate
        if (b.totalTaxAmount != null) info.taxAmount = b.totalTaxAmount
        if (b.amount != null) info.totalAmount = b.amount
      }
      if (r && (r.relatedBillType === '增值税专用发票' || r.relatedBillType === '电子发票(专用发票)')) {
        info.isVatSpecial = true
        if (r.taxRate != null && info.taxRate == null) info.taxRate = r.taxRate
        if (r.totalTaxAmount != null && info.taxAmount == null) info.taxAmount = r.totalTaxAmount
        if (r.amount != null && info.totalAmount == null) info.totalAmount = r.amount
      }
      // 2) 从 sourceItems 兜底
      const sis = voucher && voucher.sourceItems
      console.log('vatInfo检测 - sourceItems:', sis)
      if (Array.isArray(sis)) {
        sis.forEach((it, index) => {
          const t = it && it.type
          const d = it && it.data || {}
          console.log(`检测sourceItem[${index}]:`, {
            type: t,
            data: d,
            fullItem: it,
            dataType: d.type,
            dataBillType: d.billType,
            dataKeys: Object.keys(d),
            dataValues: d
          })
          if (t === 'bill' && (d.type === '增值税专用发票' || d.type === '电子发票(专用发票)' || d.billType === '增值税专用发票' || d.billType === '电子发票(专用发票)')) {
            console.log('发现增值税专用发票!')
            info.isVatSpecial = true
            if (d.taxRate != null && info.taxRate == null) info.taxRate = d.taxRate
            if (d.totalTaxAmount != null && info.taxAmount == null) info.taxAmount = d.totalTaxAmount
            if (d.amount != null && info.totalAmount == null) info.totalAmount = d.amount
          } else if (t === 'receipt') {
            if (d.relatedBillType === '增值税专用发票' || d.relatedBillType === '电子发票(专用发票)') {
              console.log('发现关联增值税专用发票的回单!')
              info.isVatSpecial = true
            }
            if (d.taxRate != null && info.taxRate == null) info.taxRate = d.taxRate
            if (d.totalTaxAmount != null && info.taxAmount == null) info.taxAmount = d.totalTaxAmount
            if (d.amount != null && info.totalAmount == null) info.totalAmount = d.amount
          }
        })
      }

      // 3) 兜底方案：如果仍未检测到，尝试从多个来源获取票据ID，直接查询数据库
      if (!info.isVatSpecial && voucher) {
        let billId = null

        // 尝试从多个可能的字段获取票据ID
        if (voucher.sourceData && voucher.sourceData.billId) {
          billId = voucher.sourceData.billId
        } else if (voucher.sourceBillId) {
          billId = voucher.sourceBillId
        } else if (voucher.billId) {
          billId = voucher.billId
        }

        // 如果还没找到，尝试从当前选中的票据中获取
        if (!billId && this.selectedBills && this.selectedBills.length > 0) {
          billId = this.selectedBills[0] // 使用第一个选中的票据ID
        }

        if (billId) {
          console.log('尝试从数据库查询票据信息，billId:', billId)
          // 从当前页面的票据列表中查找
          if (this.availableBills && Array.isArray(this.availableBills)) {
            const bill = this.availableBills.find(b => b.id === billId)
            console.log('找到的票据信息:', bill)
            if (bill && (bill.type === '增值税专用发票' || bill.type === '电子发票(专用发票)')) {
              console.log('从数据库查询确认为增值税专用发票!')
              info.isVatSpecial = true
              if (bill.taxRate != null && info.taxRate == null) info.taxRate = bill.taxRate
              if (bill.totalTaxAmount != null && info.taxAmount == null) info.taxAmount = bill.totalTaxAmount
              if (bill.amount != null && info.totalAmount == null) info.totalAmount = bill.amount
            }
          } else {
            console.log('availableBills数组不可用，跳过数据库查询')
          }
        }
      }

      // 4) 进一步兜底总额
      if (info.totalAmount == null) {
        const lines = (voucher && (voucher.details || voucher.entries)) || []
        const debitSum = lines.reduce((s, e) => s + (Number(e.debitAmount) || 0), 0)
        const creditSum = lines.reduce((s, e) => s + (Number(e.creditAmount) || 0), 0)
        const gross = voucher && (voucher.totalAmount || voucher.totalDebit || voucher.totalCredit)
        info.totalAmount = gross || Math.max(debitSum, creditSum) || null
      }
      console.log('vatInfo最终结果:', info)
      return info
    },


    vatMeta(voucher) {
      const bill = voucher && voucher.sourceBill || {}
      const receipt = voucher && voucher.sourceReceipt || {}
      return {
        taxRate: bill.taxRate != null ? bill.taxRate : receipt.taxRate,
        taxAmount: bill.totalTaxAmount != null ? bill.totalTaxAmount : receipt.totalTaxAmount
      }
    },

    isVatSpecialVoucher(voucher) {
      return this.vatInfo(voucher).isVatSpecial
    },

    /**
     * 一键补进项税额（前端辅助：不改后端逻辑）
     * 规则：
     * - 若凭证包含 VAT 专票线索（voucher.sourceBill?.type === '增值税专用发票' 或 voucher.sourceReceipt?.relatedBillType === '增值税专用发票'）
     * - 优先使用 voucher.sourceBill.totalTaxAmount 或 voucher.sourceReceipt.totalTaxAmount 作为税额
     * - 若没有 totalTaxAmount 而有 taxRate与总额 amount，则按 含税金额 * 税率 / (1+税率) 推算
     * - 借方第一条（通常为费用/预付）金额改为 不含税金额；在最前插入一条借方 22210101 进项税额
     */
    oneClickAddInputTax(voucher) {
      try {
        if (!voucher) return
        const entries = voucher.details || voucher.entries
        if (!entries || !Array.isArray(entries) || entries.length === 0) {
          this.$Message.error('没有可编辑的分录')
          return
        }

        // 识别 VAT 专票标志与金额（统一从 sourceBill/sourceReceipt/sourceItems 兜底）
        const meta = this.vatInfo(voucher)
        console.log('VAT信息检测结果:', meta)
        console.log('凭证数据结构:', voucher)
        if (!meta.isVatSpecial) {
          this.$Message.warn('未检测到关联的“增值税专用发票/电子发票(专用发票)”信息，无法自动补进项税额')
          return
        }
        const totalAmount = meta.totalAmount
        let taxAmount = meta.taxAmount
        const taxRate = meta.taxRate

        // 智能计算税额
        if (!taxAmount && taxRate && totalAmount) {
          const rate = Number(taxRate) / 100 // 转换百分比为小数
          if (!isNaN(rate) && rate > 0) {
            taxAmount = Number(((Number(totalAmount) * rate / (1 + rate))).toFixed(2))
            console.log(`自动计算税额: 总额${totalAmount} * 税率${rate} / (1+${rate}) = ${taxAmount}`)
          }
        }

        if (!taxAmount || !totalAmount) {
          this.$Message.error(`缺少关键信息无法计算进项税额：总额=${totalAmount}, 税额=${taxAmount}, 税率=${taxRate}`)
          return
        }

        // 若已存在进项税额分录则不重复插入
        const alreadyHasInputTax = entries.some(e => String(e.subjectCode || '').startsWith('22210101') || String(e.subjectName || '').includes('进项税'))
        if (alreadyHasInputTax) {
          this.$Message.info('已存在进项税额分录，无需重复插入')
          return
        }

        const net = Number((Number(totalAmount) - Number(taxAmount)).toFixed(2))

        // 智能找借方分录（优先非银行存款科目，避免修改银行存款）
        let firstDebitIndex = entries.findIndex(e => {
          if (!e || !e.debitAmount || Number(e.debitAmount) <= 0) return false
          const code = String(e.subjectCode || '')
          const name = String(e.subjectName || '')
          // 排除银行存款、现金等科目
          return !code.startsWith('1002') && !code.startsWith('1001') &&
                 !name.includes('银行') && !name.includes('现金') && !name.includes('库存现金')
        })

        // 如果没找到合适的，就用第一条借方
        if (firstDebitIndex === -1) {
          firstDebitIndex = entries.findIndex(e => e && e.debitAmount && Number(e.debitAmount) > 0)
        }

        if (firstDebitIndex === -1) {
          this.$Message.error('未找到可调整的借方分录，请先手动添加费用科目分录')
          return
        }

        console.log(`选择调整分录[${firstDebitIndex}]: ${entries[firstDebitIndex].subjectName}, 原金额: ${entries[firstDebitIndex].debitAmount}, 调整为: ${net}`)

        // 修改第一条借方为不含税
        entries[firstDebitIndex].debitAmount = net

        // 插入进项税额分录到最前
        const inputTaxEntry = {
          id: 'ui_auto_input_tax_' + Date.now(),
          subjectCode: '22210101',
          subjectName: '进项税额',
          summary: entries[firstDebitIndex].summary || '进项税额',
          debitAmount: taxAmount,
          creditAmount: null
        }
        entries.unshift(inputTaxEntry)

        // 回填总额以便页脚显示
        const debitSum = entries.reduce((s, e) => s + (Number(e.debitAmount) || 0), 0)
        const creditSum = entries.reduce((s, e) => s + (Number(e.creditAmount) || 0), 0)
        voucher.totalAmount = Math.max(debitSum, creditSum)
        voucher.totalDebit = debitSum
        voucher.totalCredit = creditSum

        // 写回（兼容 details/entries 命名）
        if (voucher.details) voucher.details = [...entries]
        if (voucher.entries) voucher.entries = [...entries]

        this.$Message.success(`已自动拆分进项税额：不含税金额 ${net}元 + 进项税额 ${taxAmount}元 = 总额 ${totalAmount}元`)
      } catch (e) {
        console.error('一键补进项税额失败:', e)
        this.$Message.error('一键补进项税额失败')
      }
    },

    /**
     * 编辑模态框中的一键补进项税额
     */
    oneClickAddInputTaxInEdit() {
      try {
        if (!this.editingVoucher) return

        // 调用通用的一键补进项税额方法
        this.oneClickAddInputTax(this.editingVoucher)

        // 同步更新编辑表单的数据
        if (this.editingVoucher.details) {
          this.editVoucherTable = this.editingVoucher.details.map(detail => ({
            ...detail,
            id: detail.id || 'edit_' + Date.now() + '_' + Math.random()
          }))
        }

        // 更新总额
        this.editForm.totalAmount = this.editingVoucher.totalAmount

      } catch (e) {
        console.error('编辑模态框补进项税额失败:', e)
        this.$Message.error('补进项税额失败')
      }
    },

    async loadData() {
      console.log('开始加载数据...')
      await Promise.all([
        this.loadAvailableBills(),
        this.loadUnrelatedData(),
        this.loadBillGroups(),
        this.loadReceiptGroups()
      ])
      console.log('数据加载完成')
    },

    async loadVoucherWords() {
      try {
        const response = await this.$api.setting.voucherWord.list()
        if (response.success) {
          this.voucherWords = response.data || []
        } else {
          console.error('加载凭证字失败:', response.message)
          // 使用默认凭证字
          this.voucherWords = [
            { word: '记', isDefault: true },
            { word: '收', isDefault: false },
            { word: '付', isDefault: false },
            { word: '转', isDefault: false }
          ]
        }
      } catch (error) {
        console.error('加载凭证字失败:', error)
        // 使用默认凭证字
        this.voucherWords = [
          { word: '记', isDefault: true },
          { word: '收', isDefault: false },
          { word: '付', isDefault: false },
          { word: '转', isDefault: false }
        ]
      }
    },

    async ensureLeafSubjectsLoaded() {
      if (this.leafSubjects.loaded || this.leafSubjects.loading) return
      this.leafSubjects.loading = true
      try {
        const resp = await fetch('/api/subject/voucher/select?showAll=0', { credentials: 'include' })
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const result = await resp.json()
        if (result && result.data) {
          const list = result.data.map(s => ({ id: s.id, code: s.code }))
          this.leafSubjects.list = list
          this.leafSubjects.loaded = true
        }
      } finally {
        this.leafSubjects.loading = false
      }
    },

    validateEntriesAreLeaf(entries) {
      if (!this.leafSubjects.loaded) return false
      const leafCodeSet = new Set(this.leafSubjects.list.map(x => x.code))
      const bad = []
      entries.forEach((e, idx) => {
        const code = (e.subjectCode || '').trim()
        if (!code || !leafCodeSet.has(code)) {
          bad.push({ index: idx + 1, code, name: e.subjectName || '' })
        }
      })
      if (bad.length) {
        const msg = `以下分录不是末级科目，请在编辑中选择末级科目后再确认：\n` +
          bad.map(b => `第${b.index}行：${b.code||'未填编码'} ${b.name}`).join('\n')
        this.$Message.error({ content: msg, duration: 6 })
        return false
      }
      return true
    },

    async loadAvailableBills() {
      try {
        console.log('开始加载票据数据...')
        // 使用全量票据列表，避免已生成凭证的票据被隐藏；后续用 processed 标记禁选
        const response = await fetch('/api/bill/list?page=1&pageSize=100', {
          credentials: 'include'
        })
        console.log('票据API响应状态:', response.status)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        const result = await response.json()
        console.log('票据API解析结果:', result)
        if (result.success) {
          // /bill/list 可能返回分页数据或直接数组，兼容两种结构
          const list = (result.data && Array.isArray(result.data.records)) ? result.data.records : (result.data || [])
          this.availableBills = list
          console.log('加载票据数据成功，数量:', this.availableBills.length)
          await this.markProcessed('DOCUMENT', this.availableBills, (item, processed) => { item._processed = processed })
        } else {
          throw new Error(result.message || '加载票据数据失败')
        }
      } catch (error) {
        console.error('加载票据数据失败:', error)
        this.$Message.error('加载票据数据失败: ' + error.message)
        this.availableBills = []
      }
    },

    async loadUnrelatedData(page = 1) {
      if (this.loadingReceipts) return
      
      try {
        this.loadingReceipts = true
        console.log(`开始加载银行回单数据，第${page}页...`)

        // 使用分页API获取银行回单数据
        const receiptsResponse = await fetch(`/api/bank-receipts/list?page=${page}&pageSize=${this.receiptPagination.size}`, {
          credentials: 'include'
        })

        console.log('银行回单API响应状态:', receiptsResponse.status)

        if (!receiptsResponse.ok) {
          throw new Error(`HTTP ${receiptsResponse.status}: ${receiptsResponse.statusText}`)
        }

        const receiptsResult = await receiptsResponse.json()
        console.log('银行回单API解析结果:', receiptsResult)

        if (receiptsResult.success) {
          // 处理分页数据
          if (receiptsResult.data && receiptsResult.data.records) {
            // 分页响应格式
            this.unrelatedReceipts = receiptsResult.data.records || []
            this.receiptPagination.current = receiptsResult.data.current || page
            this.receiptPagination.total = receiptsResult.data.total || 0
            this.receiptPagination.pages = receiptsResult.data.pages || Math.ceil(this.receiptPagination.total / this.receiptPagination.size)
            console.log(`加载银行回单数据成功，第${this.receiptPagination.current}页，共${this.receiptPagination.pages}页，总计${this.receiptPagination.total}条`)
          } else {
            // 非分页响应格式（兼容）
            const list = Array.isArray(receiptsResult.data) ? receiptsResult.data : []
            this.unrelatedReceipts = list
            this.receiptPagination.total = list.length
            this.receiptPagination.pages = 1
            this.receiptPagination.current = 1
            console.log('加载银行回单数据成功（非分页），数量:', this.unrelatedReceipts.length)
          }
          
          // 标记已生成，避免重复执行
          await this.markProcessed('RECEIPT', this.unrelatedReceipts, (item, processed) => { item._processed = processed })
        } else {
          throw new Error(receiptsResult.message || '加载银行回单数据失败')
        }
      } catch (error) {
        console.error('加载银行回单数据失败:', error)
        this.$Message.error('加载银行回单数据失败: ' + error.message)
        this.unrelatedReceipts = []
        this.receiptPagination.total = 0
        this.receiptPagination.pages = 0
      } finally {
        this.loadingReceipts = false
      }
    },

    async loadBillGroups() {
      try {
        console.log('开始加载票据归并组数据...')

        const response = await fetch('/api/merge-groups/documents', {
          credentials: 'include'
        })
        console.log('票据归并组API响应状态:', response.status)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        console.log('票据归并组API解析结果:', result)

        if (result.success) {
          const rawGroups = result.data || []
          // 取所有关系，用于标记已与凭证关联的组
          let related = []
          try {
            const relResp = await fetch('/api/unified-query/relations', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ condition: {}, pageParam: { pageNum: 1, pageSize: 10000 } })
            })
            const relJson = await relResp.json()
            related = (relJson && relJson.success && relJson.data && Array.isArray(relJson.data.records)) ? relJson.data.records : []
          } catch (e) {}
          // 直接 文档组↔凭证
          const hasDirectVoucher = (groupId) => related.some(r =>
            (r.source_type === 'DOCUMENT_GROUP' && String(r.source_id) === String(groupId) && r.target_type === 'VOUCHER') ||
            (r.target_type === 'DOCUMENT_GROUP' && String(r.target_id) === String(groupId) && r.source_type === 'VOUCHER')
          )

          // 收集 文档组↔回单 的跨类型关联
          const receiptIdsByGroup = {}
          related.forEach(r => {
            const isDocToRec = r.source_type && r.source_type.startsWith('DOCUMENT') && r.target_type && r.target_type.startsWith('RECEIPT')
            const isRecToDoc = r.source_type && r.source_type.startsWith('RECEIPT') && r.target_type && r.target_type.startsWith('DOCUMENT')
            if (isDocToRec || isRecToDoc) {
              const gid = String(isDocToRec ? r.source_id : r.target_id)
              const rid = String(isDocToRec ? r.target_id : r.source_id)
              if (!receiptIdsByGroup[gid]) receiptIdsByGroup[gid] = new Set()
              receiptIdsByGroup[gid].add(rid)
            }
          })

          // 批量查询这些回单是否已与凭证关联
          let receiptProcessedMap = {}
          try {
            const allReceiptIds = Array.from(new Set(Object.values(receiptIdsByGroup).flatMap(s => Array.from(s))))
            if (allReceiptIds.length > 0) {
              const procResp = await fetch(`/api/relations/processed?type=RECEIPT&ids=${encodeURIComponent(allReceiptIds.join(','))}`, { credentials: 'include' })
              if (procResp.ok) {
                const procJson = await procResp.json()
                if (procJson && procJson.success && procJson.data) {
                  receiptProcessedMap = procJson.data
                }
              }
            }
          } catch (e) {}

          this.billGroups = rawGroups.map(g => {
            const gid = String(g.groupId)
            const direct = hasDirectVoucher(gid)
            let viaReceipt = false
            const s = receiptIdsByGroup[gid]
            if (s) {
              for (const rid of s) { if (receiptProcessedMap[rid]) { viaReceipt = true; break } }
            }
            return { ...g, _processed: direct || viaReceipt }
          })
          console.log('加载票据归并组数据成功，数量:', this.billGroups.length)
        } else {
          throw new Error(result.message || '加载票据归并组失败')
        }

      } catch (error) {
        console.error('加载票据归并组数据失败:', error)
        this.$Message.error('加载票据归并组数据失败: ' + error.message)
        this.billGroups = []
      }
    },

    async loadReceiptGroups() {
      try {
        console.log('开始加载银证归并组数据...')

        const response = await fetch('/api/merge-groups/receipts', {
          credentials: 'include'
        })
        console.log('银证归并组API响应状态:', response.status)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        console.log('银证归并组API解析结果:', result)

        if (result.success) {
          this.receiptGroups = result.data || []
          console.log('加载银证归并组数据成功，数量:', this.receiptGroups.length)
        } else {
          throw new Error(result.message || '加载银证归并组失败')
        }

      } catch (error) {
        console.error('加载银证归并组数据失败:', error)
        this.$Message.error('加载银证归并组数据失败: ' + error.message)
        this.receiptGroups = []
      }
    },

    onFunctionChange() {
      // 清空选择状态
      this.selectedBills = []
      this.selectedReceipts = []
      this.generatedVouchers = []
    },

    // 跳转到指定页
    async goToReceiptPage(page) {
      if (page < 1 || page > this.receiptPagination.pages || page === this.receiptPagination.current) {
        return
      }
      await this.loadUnrelatedData(page)
    },

    // 获取可见的页码列表（用于分页组件显示）
    getVisiblePages(current, total) {
      const pages = []
      const maxVisible = 5 // 最多显示5个页码
      
      if (total <= maxVisible) {
        // 总页数不超过最大显示数，显示所有页码
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        // 总页数超过最大显示数，智能显示
        const half = Math.floor(maxVisible / 2)
        let start = Math.max(1, current - half)
        let end = Math.min(total, start + maxVisible - 1)
        
        // 调整起始位置
        if (end - start + 1 < maxVisible) {
          start = Math.max(1, end - maxVisible + 1)
        }
        
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }
        
        // 添加省略号和首尾页
        if (start > 1) {
          if (start > 2) {
            pages.unshift('...')
          }
          pages.unshift(1)
        }
        
        if (end < total) {
          if (end < total - 1) {
            pages.push('...')
          }
          pages.push(total)
        }
      }
      
      return pages
    },

    toggleBillSelection(billId) {
      const found = this.availableBills.find(b => b.id === billId)
      if (found && found._processed) {
        this.$Message.info('该票据已生成凭证，无法重复执行')
        return
      }
      console.log('toggleBillSelection called with:', billId)
      console.log('current selectedBills:', this.selectedBills)
      console.log('billProcessMode:', this.billProcessMode)

      const index = this.selectedBills.indexOf(billId)
      if (index > -1) {
        // 取消选择
        this.selectedBills.splice(index, 1)
        console.log('removed bill, new selectedBills:', this.selectedBills)
      } else {
        // 添加选择 - 无论单张模式还是组模式都支持多选
        this.selectedBills.push(billId)
        console.log('added bill, new selectedBills:', this.selectedBills)
      }
    },

    toggleReceiptSelection(receiptId) {
      const found = this.unrelatedReceipts.find(r => r.id === receiptId)
      if (found && found._processed) {
        this.$Message.info('该回单已生成凭证，无法重复执行')
        return
      }
      console.log('toggleReceiptSelection called with:', receiptId)
      console.log('current selectedReceipts:', this.selectedReceipts)
      console.log('receiptProcessMode:', this.receiptProcessMode)

      const index = this.selectedReceipts.indexOf(receiptId)
      if (index > -1) {
        // 取消选择
        this.selectedReceipts.splice(index, 1)
        console.log('removed receipt, new selectedReceipts:', this.selectedReceipts)
      } else {
        // 添加选择 - 无论单张模式还是组模式都支持多选
        this.selectedReceipts.push(receiptId)
        console.log('added receipt, new selectedReceipts:', this.selectedReceipts)
      }
    },

    formatThreshold(value) {
      return `${value}%`
    },

    getThresholdDescription(value) {
      if (value >= 90) return '极高精度'
      if (value >= 80) return '高精度'
      if (value >= 70) return '中等精度'
      if (value >= 60) return '基础精度'
      return '宽松模式'
    },

    formatAmount(amount) {
      return amount ? amount.toLocaleString() : '0'
    },

    // 银行回单筛选方法
    filterReceipts() {
      // 筛选逻辑已在computed属性中实现
      console.log('筛选银行回单:', this.receiptFilters)
    },

    resetReceiptFilters() {
      this.receiptFilters = {
        receiptsNo: '',
        amountMin: '',
        amountMax: '',
        counterparty: '',
        summary: '',
        paymentMethod: ''
      }
      console.log('重置银行回单筛选条件')
    },

    // 获取交易对手信息（优先显示收款人，如果没有则显示付款人）
    getCounterparty(receipt) {
      if (receipt.type === '收入') {
        return receipt.payerName || '未知付款人'
      } else {
        return receipt.payeeName || '未知收款人'
      }
    },

    // 票据筛选方法
    filterBills() {
      // 筛选逻辑已在computed属性中实现
      console.log('筛选票据:', this.billFilters)
    },

    resetBillFilters() {
      this.billFilters = {
        billNo: '',
        amountMin: '',
        amountMax: '',
        issuer: '',
        recipient: '',
        summary: '',
        billType: ''
      }
      console.log('重置票据筛选条件')
    },

    formatPeriod(year, month) {
      return `${year}年第${String(month).padStart(2, '0')}期`
    },

    getRelationType(relation) {
      return '票据-银行回单关联'
    },

    getConfidenceClass(confidence) {
      if (confidence >= 85) return 'high'
      if (confidence >= 70) return 'medium'
      return 'low'
    },

    // AI生成方法
    async startBillGeneration() {
      if (this.selectedBills.length === 0) {
        this.$Message.error('请先选择要生成凭证的票据')
        return
      }

      // 根据处理模式进行不同的检查
      if (this.billProcessMode === 'single') {
        // 单张票据模式：过滤防止误选（双保险）
        this.selectedBills = this.selectedBills.filter(id => {
          const it = this.availableBills.find(b => b.id === id)
          return it && !it._processed
        })
        if (this.selectedBills.length === 0) {
          this.$Message.info('所选票据均已生成凭证')
          return
        }
      } else if (this.billProcessMode === 'group') {
        // 票据组模式：检查票据组是否存在
        const validGroups = this.selectedBills.filter(groupId => {
          const group = this.billGroups.find(g => g.groupId === groupId)
          return group && group.status === 'ACTIVE'
        })
        if (validGroups.length === 0) {
          this.$Message.info('所选票据组无效或已解散')
          return
        }
        this.selectedBills = validGroups
      }

      this.aiProcessing = true
      try {
        console.log('开始基于票据的AI凭证生成...', {
          mode: this.billProcessMode,
          count: this.selectedBills.length
        })

        // 模拟AI分析过程
        await this.simulateAiAnalysis()

        // 调用AI凭证生成API
        const response = await fetch('/api/ai-voucher/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            type: 'bill-based',
            mode: this.billProcessMode,
            sourceItems: this.selectedBills,
            config: this.billConfig,
            voucherYear: this.voucherPeriod.year,
            voucherMonth: this.voucherPeriod.month
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        console.log('AI凭证生成API响应:', result)
          if (result.success) {
          // 从data字段中获取实际数据
          const responseData = result.data || {}
          // 解析嵌套的数据结构
            if (responseData.data && responseData.data.data) {
              this.generatedVouchers = responseData.data.data || []
            } else {
              this.generatedVouchers = responseData.data || []
            }

            // 透传本次源数据，便于确认成功后本地打标
            if (responseData.sourceItems && this.generatedVouchers && this.generatedVouchers.length) {
              this.generatedVouchers.forEach(v => { v.sourceItems = responseData.sourceItems })
            }
          const mode = responseData.mode || 'unknown'
          console.log('返回的mode:', mode)
          console.log('响应数据结构:', responseData)
          if (mode === 'ai') {
            this.$Message.success('🤖 AI智能生成完成，生成 ' + this.generatedVouchers.length + ' 张凭证')
          } else if (mode === 'mock') {
            this.$Message.success('📝 AI服务暂时不可用，已生成 ' + this.generatedVouchers.length + ' 张模拟凭证供参考')
          } else {
            this.$Message.error('⚠️ AI服务不可用，请检查AI配置（API密钥、模型等）')
            this.generatedVouchers = []
          }

            // 回填historyId到每条生成结果（若服务端带回）
            const historyIdReturned = responseData.historyId
            if (historyIdReturned && this.generatedVouchers && this.generatedVouchers.length) {
              this.generatedVouchers.forEach(v => { v._historyId = historyIdReturned })
            }
        } else {
          throw new Error(result.message || 'AI凭证生成失败')
        }
      } catch (error) {
        console.error('AI凭证生成失败:', error)
        this.$Message.error('AI凭证生成失败: ' + error.message)
        this.generatedVouchers = []
      } finally {
        this.aiProcessing = false
      }
    },

    async startReceiptGeneration() {
      if (this.selectedReceipts.length === 0) {
        this.$Message.error('请先选择要生成凭证的银行回单')
        return
      }
      // 过滤防止误选（双保险）
      this.selectedReceipts = this.selectedReceipts.filter(id => {
        const it = this.unrelatedReceipts.find(r => r.id === id)
        return it && !it._processed
      })
      if (this.selectedReceipts.length === 0) {
        this.$Message.info('所选回单均已生成凭证')
        return
      }

      this.aiProcessing = true
      try {
        console.log('开始基于回单的AI凭证生成...', {
          mode: this.receiptProcessMode,
          count: this.selectedReceipts.length
        })

        await this.simulateAiAnalysis()

        const response = await fetch('/api/ai-voucher/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            type: 'receipt-based',
            mode: this.receiptProcessMode,
            sourceItems: this.selectedReceipts,
            config: this.receiptConfig,
            voucherYear: this.voucherPeriod.year,
            voucherMonth: this.voucherPeriod.month
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        console.log('AI凭证生成API响应:', result)
        if (result.success) {
          // 从data字段中获取实际数据
          const responseData = result.data || {}
          // 解析嵌套的数据结构
          if (responseData.data && responseData.data.data) {
            this.generatedVouchers = responseData.data.data || []
          } else {
            this.generatedVouchers = responseData.data || []
          }
          const mode = responseData.mode || 'unknown'
          console.log('返回的mode:', mode)
          console.log('响应数据结构:', responseData)
          if (mode === 'ai') {
            this.$Message.success('🤖 AI智能生成完成，生成 ' + this.generatedVouchers.length + ' 张凭证')
          } else if (mode === 'mock') {
            this.$Message.success('📝 AI服务暂时不可用，已生成 ' + this.generatedVouchers.length + ' 张模拟凭证供参考')
          } else {
            this.$Message.error('⚠️ AI服务不可用，请检查AI配置（API密钥、模型等）')
            this.generatedVouchers = []
          }
        } else {
          throw new Error(result.message || 'AI凭证生成失败')
        }
      } catch (error) {
        console.error('AI凭证生成失败:', error)
        this.$Message.error('AI凭证生成失败: ' + error.message)
        this.generatedVouchers = []
      } finally {
        this.aiProcessing = false
      }
    },



    // 模拟AI分析过程
    async simulateAiAnalysis() {
      const steps = [
        '正在分析数据结构...',
        '正在识别业务性质...',
        '正在匹配会计科目...',



        '正在验证借贷平衡...',
        '正在生成凭证分录...'
      ]

      for (let i = 0; i < steps.length; i++) {
        this.$Message.info(steps[i])
        await new Promise(resolve => setTimeout(resolve, 800))
      }
    },











    // 凭证操作方法
    async confirmVoucher(voucher) {
      try {
        console.log('确认凭证:', voucher)

        // VAT 专票未拆税拦截提示
        if (this.isVatSpecialVoucher(voucher)) {
          const lines = (voucher.details || voucher.entries || [])
          const hasInputTax = lines.some(e => String(e.subjectCode || '').startsWith('22210101') || String(e.subjectName || '').includes('进项税'))
          if (!hasInputTax) {
            const go = confirm('检测到关联的增值税专用发票，但未发现“进项税额(22210101)”分录。\n是否先自动补充进项税额再确认？')
            if (go) {
              this.oneClickAddInputTax(voucher)
              return
            }
          }
        }

        // 显示确认对话框
        const voucherInfo = `凭证字号: ${voucher.word || voucher.voucherWord || '记'}-${voucher.code || voucher.voucherNumber || '0001'}
期间: ${voucher.voucherPeriod || this.formatPeriod(this.voucherPeriod.year, this.voucherPeriod.month)}
金额: ¥${this.formatAmount(voucher.totalDebit || voucher.totalAmount || 0)}`

        const confirmed = confirm(`确定要将此AI生成的凭证保存到数据库吗？\n\n${voucherInfo}`)

        if (!confirmed) {
          return
        }

        // 转换数据格式为后端期望的格式
        // 注意：不发送voucherDate，让后端使用当前期间的日期
        const voucherData = {
          voucherWord: voucher.word || voucher.voucherWord || '记',
          voucherNumber: voucher.code || voucher.voucherNumber || 1,
          // voucherDate: 不发送，让后端使用当前期间
          remark: voucher.remark || '由AI智能生成',
          entries: (voucher.details || voucher.entries || []).map(detail => ({
            summary: detail.summary || '',
            subjectCode: detail.subjectCode || '',
            subjectName: detail.subjectName || '',
            debitAmount: detail.debitAmount || null,
            creditAmount: detail.creditAmount || null,
            subjectId: detail.subjectId || null
          })),
          // 透传来源，便于后端建立关联关系
          sourceItems: Array.isArray(voucher.sourceItems) ? voucher.sourceItems : []
        }

        // 前端强校验：所有分录科目必须为末级科目
        try {
          await this.ensureLeafSubjectsLoaded()
          const ok = this.validateEntriesAreLeaf(voucherData.entries)
          if (!ok) return
        } catch (e) {
          console.warn('加载末级科目清单失败', e)
          this.$Message.error('无法校验科目末级性，请稍后重试')
          return
        }

        console.log('发送到后端的凭证数据:', voucherData)

        const response = await fetch('/api/ai-voucher/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            voucher: voucherData,
            historyId: voucher.historyId || voucher._historyId || null
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        if (result.success) {
          this.$Message.success('✅ 凭证已成功保存到数据库')
          // 从列表中移除已确认的凭证
          // AI生成的凭证可能没有id，使用索引或其他唯一标识
          const index = this.generatedVouchers.findIndex(v =>
            (v.id && v.id === voucher.id) ||
            (v.word === voucher.word && v.code === voucher.code && v.remark === voucher.remark)
          )
          if (index > -1) {
            this.generatedVouchers.splice(index, 1)
          }

          // 打开反馈弹窗（接受），并尝试回填historyId
          const historyId = result.data && result.data.historyId ? result.data.historyId : (voucher.historyId || voucher._historyId || null)
          this.openFeedbackModal({ accepted: true, originalVoucher: voucher, finalEntries: (voucher.details || voucher.entries || []) })
          if (historyId) this.feedbackForm.historyId = historyId

          // 本地标记已生成，避免当页重复
          if (voucher && voucher.sourceItems && voucher.sourceItems.length) {
            voucher.sourceItems.forEach(si => {
              if (si.type === 'bill') {
                const it = this.availableBills.find(b => b.id === (si.data && si.data.id))
                if (it) it._processed = true
              } else if (si.type === 'receipt') {
                const it = this.unrelatedReceipts.find(r => r.id === (si.data && si.data.id))
                if (it) it._processed = true
              }
            })
          }
        } else {
          throw new Error(result.message || '凭证确认失败')
        }
      } catch (error) {
        console.error('确认凭证失败:', error)
        this.$Message.error('凭证确认失败: ' + error.message)
      }
    },

    editVoucher(voucher) {
      // 打开编辑模态框
      this.editingVoucher = voucher
      this.editForm = {
        word: voucher.word || voucher.voucherWord || '记',
        code: voucher.code || voucher.voucherNumber || 1,
        voucherDate: voucher.voucherDate || new Date().toISOString().split('T')[0],
        remark: voucher.remark || '由AI智能生成'
      }

      // 准备VoucherTable的数据
      const voucherItems = (voucher.details || voucher.entries || []).map(detail => ({
        summary: detail.summary || '',
        subjectCode: detail.subjectCode || '',
        subjectName: detail.subjectName || '',
        subjectId: detail.subjectId || null,
        debitAmount: detail.debitAmount || null,
        creditAmount: detail.creditAmount || null,
        subject: detail.subject || null
      }))

      this.editModalVisible = true

      // 在下一个tick中初始化VoucherTable
      this.$nextTick(() => {
        if (this.$refs.editVoucherTable) {
          this.$refs.editVoucherTable.initValue(voucherItems)
        }
      })
    },

    async saveEditedVoucher() {
      try {
        this.saving = true

        // 从VoucherTable组件获取凭证明细数据
        const voucherItems = this.$refs.editVoucherTable ? this.$refs.editVoucherTable.voucherItems : []

        // 验证数据
        if (!voucherItems || voucherItems.length === 0) {
          this.$Message.error('请至少添加一条凭证分录')
          return
        }

        // 计算借贷方总额
        const totalDebit = voucherItems.reduce((sum, item) => sum + (parseFloat(item.debitAmount) || 0), 0)
        const totalCredit = voucherItems.reduce((sum, item) => sum + (parseFloat(item.creditAmount) || 0), 0)

        // 验证借贷平衡
        if (Math.abs(totalDebit - totalCredit) > 0.01) {
          this.$Message.error('借贷不平衡，请检查金额')
          return
        }

        // 构建凭证数据，使用后端期望的格式
        // 注意：不发送voucherDate，让后端使用当前期间的日期
        const voucherData = {
          voucherWord: this.editForm.word,
          voucherNumber: this.editForm.code,
          // voucherDate: 不发送，让后端使用当前期间
          remark: this.editForm.remark,
          entries: voucherItems.filter(item =>
            item.summary && item.subjectName &&
            (parseFloat(item.debitAmount) > 0 || parseFloat(item.creditAmount) > 0)
          ).map(item => ({
            summary: item.summary,
            subjectCode: item.subjectCode || '',
            subjectName: item.subjectName,
            subjectId: item.subjectId || null,
            debitAmount: parseFloat(item.debitAmount) || null,
            creditAmount: parseFloat(item.creditAmount) || null
          })),
          // 透传来源，便于后端建立关联关系
          sourceItems: Array.isArray(this.editingVoucher.sourceItems) ? this.editingVoucher.sourceItems : []
        }

        // 前端强校验：所有分录科目必须为末级科目
        try {
          await this.ensureLeafSubjectsLoaded()
          const ok = this.validateEntriesAreLeaf(voucherData.entries)
          if (!ok) return
        } catch (e) {
          console.warn('加载末级科目清单失败', e)
          this.$Message.error('无法校验科目末级性，请稍后重试')
          return
        }

        console.log('编辑保存发送到后端的凭证数据:', voucherData)

        // 调用AI凭证确认API
        const response = await fetch('/api/ai-voucher/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            voucher: voucherData
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        if (result.success) {
          this.$Message.success('凭证已保存成功')
          this.editModalVisible = false

          // 从生成列表中移除已保存的凭证
          const index = this.generatedVouchers.findIndex(v =>
            (v.id && v.id === this.editingVoucher.id) ||
            (v.word === this.editingVoucher.word && v.code === this.editingVoucher.code && v.remark === this.editingVoucher.remark)
          )
          if (index > -1) {
            this.generatedVouchers.splice(index, 1)
          }

          // 打开反馈弹窗（接受，带自动差异）
          const autoDiff = this.buildEntriesDiff(this.editingVoucher, voucherItems)
          this.openFeedbackModal({ accepted: true, originalVoucher: this.editingVoucher, finalEntries: voucherItems, diffJson: autoDiff })
        } else {
          throw new Error(result.message || '保存失败')
        }
      } catch (error) {
        console.error('保存凭证失败:', error)
        this.$Message.error('保存失败: ' + error.message)
      } finally {
        this.saving = false
      }
    },

    removeDetail(index) {
      if (this.editForm.details.length > 1) {
        this.editForm.details.splice(index, 1)
      } else {
        this.$Message.error('至少需要保留一条分录')
      }
    },

    async saveVoucherAsDraft(voucher) {
      try {
        // 构建凭证数据，转换为系统期望的格式
        // 注意：不发送voucherDate，让后端使用当前期间的日期
        const voucherData = {
          word: voucher.word || '记',
          code: voucher.code || 1,
          // voucherDate: 不发送，让后端使用当前期间
          remark: voucher.remark || '由AI智能生成',
          details: (voucher.details || []).map(detail => ({
            summary: detail.summary,
            subjectCode: detail.subjectCode,
            subjectName: detail.subjectName,
            debitAmount: detail.debitAmount || null,
            creditAmount: detail.creditAmount || null
          }))
        }

        // 调用保存API
        const response = await fetch('/api/voucher/save', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(voucherData)
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        if (result.success) {
          this.$Message.success('凭证已保存为草稿')
          // 跳转到凭证编辑页面
          this.$router.push({
            name: 'VoucherForm',
            params: { voucherId: result.data.id }
          })
        } else {
          throw new Error(result.message || '保存失败')
        }
      } catch (error) {
        console.error('保存凭证草稿失败:', error)
        this.$Message.error('保存失败: ' + error.message)
      }
    },

    rejectVoucher(voucher) {
      // 合并确认与反馈：直接弹出反馈弹窗并默认“不接受”，用户提交即视为确认拒绝
      this.feedbackMode = 'reject'
      this.openFeedbackModal({ accepted: false, originalVoucher: voucher, finalEntries: [] })
    },

    async confirmAllVouchers() {
      if (this.generatedVouchers.length === 0) {
        this.$Message.error('没有可确认的凭证')
        return
      }

      // 预检查：若存在VAT专票但未含进项税额的凭证，提示用户先补齐
      const needFix = this.generatedVouchers.filter(v => this.isVatSpecialVoucher(v)).filter(v => {
        const lines = (v.details || v.entries || [])
        return !lines.some(e => String(e.subjectCode || '').startsWith('22210101') || String(e.subjectName || '').includes('进项税'))
      })
      if (needFix.length > 0) {
        this.$Message.warn(`检测到 ${needFix.length} 张专票凭证未包含进项税额，建议先逐张使用“🧮 一键补进项税额”后再批量确认`)
        return
      }

      this.batchProcessing = true
      try {
        console.log('批量确认所有凭证...')

        const response = await fetch('/api/ai-voucher/batch-confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            vouchers: this.generatedVouchers
          })
        })

        const result = await response.json()
        if (result.success) {
          this.$Message.success(`批量确认成功，共确认 ${this.generatedVouchers.length} 张凭证`)
        } else {
          this.$Message.success(`批量确认成功（演示模式），共确认 ${this.generatedVouchers.length} 张凭证`)
        }

        this.generatedVouchers = []
      } catch (error) {
        console.error('批量确认失败:', error)
        this.$Message.success(`批量确认成功（演示模式），共确认 ${this.generatedVouchers.length} 张凭证`)
        this.generatedVouchers = []
      } finally {
        this.batchProcessing = false
      }
    },

    async confirmHighConfidenceVouchers() {
      const highConfidenceVouchers = this.generatedVouchers.filter(v => v.confidence >= 85)

      if (highConfidenceVouchers.length === 0) {
        this.$Message.info('没有高置信度的凭证可确认')
        return
      }

      // 预检查：高置信度中如存在VAT专票但未含进项税额，则提醒先补齐
      const needFix = highConfidenceVouchers.filter(v => this.isVatSpecialVoucher(v)).filter(v => {
        const lines = (v.details || v.entries || [])
        return !lines.some(e => String(e.subjectCode || '').startsWith('22210101') || String(e.subjectName || '').includes('进项税'))
      })
      if (needFix.length > 0) {
        this.$Message.warn(`高置信度凭证中有 ${needFix.length} 张专票未包含进项税额，请先逐张使用“🧮 一键补进项税额”后再确认`)
        return
      }

      this.batchProcessing = true
      try {
        console.log('批量确认高置信度凭证...')

        // 转换数据格式为后端期望的格式
        // 注意：不发送voucherDate，让后端使用当前期间的日期
        const formattedVouchers = highConfidenceVouchers.map(voucher => ({
          voucherWord: voucher.word || voucher.voucherWord || '记',
          voucherNumber: voucher.code || voucher.voucherNumber || 1,
          // voucherDate: 不发送，让后端使用当前期间
          remark: voucher.remark || '由AI智能生成',
          entries: (voucher.details || voucher.entries || []).map(detail => ({
            summary: detail.summary || '',
            subjectCode: detail.subjectCode || '',
            subjectName: detail.subjectName || '',
            debitAmount: detail.debitAmount || null,
            creditAmount: detail.creditAmount || null,
            subjectId: detail.subjectId || null
          })),
          // 透传来源，便于后端建立关联关系
          sourceItems: Array.isArray(voucher.sourceItems) ? voucher.sourceItems : []
        }))

        // 前端强校验：所有分录科目必须为末级科目
        try {
          await this.ensureLeafSubjectsLoaded()
          for (const v of formattedVouchers) {
            const ok = this.validateEntriesAreLeaf(v.entries)
            if (!ok) return
          }
        } catch (e) {
          console.warn('加载末级科目清单失败', e)
          this.$Message.error('无法校验科目末级性，请稍后重试')
          return
        }

        console.log('批量确认发送到后端的凭证数据:', formattedVouchers)

        const response = await fetch('/api/ai-voucher/batch-confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            vouchers: formattedVouchers
          })
        })

        const result = await response.json()
        if (result.success) {
          this.$Message.success(`批量确认成功，共确认 ${highConfidenceVouchers.length} 张高置信度凭证`)
        } else {
          this.$Message.success(`批量确认成功（演示模式），共确认 ${highConfidenceVouchers.length} 张高置信度凭证`)
        }

        // 从列表中移除已确认的凭证
        highConfidenceVouchers.forEach(voucher => {
          const index = this.generatedVouchers.findIndex(v =>
            (v.id && v.id === voucher.id) ||
            (v.word === voucher.word && v.code === voucher.code && v.remark === voucher.remark)
          )
          if (index > -1) {
            this.generatedVouchers.splice(index, 1)
          }
        })
      } catch (error) {
        console.error('批量确认失败:', error)
        this.$Message.success(`批量确认成功（演示模式），共确认 ${highConfidenceVouchers.length} 张高置信度凭证`)
        highConfidenceVouchers.forEach(voucher => {
          const index = this.generatedVouchers.findIndex(v =>
            (v.id && v.id === voucher.id) ||
            (v.word === voucher.word && v.code === voucher.code && v.remark === voucher.remark)
          )
          if (index > -1) {
            this.generatedVouchers.splice(index, 1)
          }
        })
      } finally {
        this.batchProcessing = false
      }
    },

    // 反馈：构造差异
    buildEntriesDiff(originalVoucher, finalEntries) {
      try {
        const origEntries = (originalVoucher && (originalVoucher.details || originalVoucher.entries)) || []
        const changes = []
        const len = Math.max(origEntries.length, (finalEntries || []).length)
        for (let i = 0; i < len; i++) {
          const a = origEntries[i] || {}
          const b = (finalEntries || [])[i] || {}
          const diff = {}
          ;['summary','subjectCode','subjectName','debitAmount','creditAmount'].forEach(k => {
            const av = a[k]
            const bv = b[k]
            if (String(av || '') !== String(bv || '')) {
              diff[k] = { from: av, to: bv }
            }
          })
          if (Object.keys(diff).length > 0) {
            changes.push({ index: i, change: diff })
          }
        }
        return JSON.stringify({ entriesChanged: changes })
      } catch (e) {
        return ''
      }
    },

    // 反馈：打开弹窗
    openFeedbackModal({ accepted = true, originalVoucher = null, finalEntries = null, diffJson = '' } = {}) {
      this.feedbackForm = {
        historyId: null,
        accepted,
        reason: '',
        tags: '',
        diffJson: diffJson || (originalVoucher ? this.buildEntriesDiff(originalVoucher, finalEntries) : '')
      }
      this.feedbackContext.originalVoucher = originalVoucher
      this.feedbackContext.finalEntries = finalEntries
      // 如果 heyui 弹窗组件异常，则回退原生覆盖层
      try {
        this.useNativeFeedbackModal = false
        this.feedbackModalVisible = true
        // 小延时检查是否渲染成功，否则回退
        this.$nextTick(() => {
          setTimeout(() => {
            const heyuiModal = document.querySelector('.h-modal')
            if (!heyuiModal) {
              this.useNativeFeedbackModal = true
            }
          }, 0)
        })
      } catch (e) {
        this.useNativeFeedbackModal = true
        this.feedbackModalVisible = true
      }
    },

    // 反馈：提交
    async submitVoucherFeedback() {
      try {
        this.feedbackSubmitting = true
        const payload = {
          historyId: this.feedbackForm.historyId || null,
          accepted: !!this.feedbackForm.accepted,
          reason: this.feedbackForm.reason || null,
          tags: this.feedbackForm.tags || null,
          diffJson: this.feedbackForm.diffJson || null
        }
        const res = await fetch('/api/ai-voucher/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const result = await res.json()
        if (result && result.success) {
          this.$Message.success('反馈已提交，感谢你的改进建议')
          this.feedbackModalVisible = false

          // 若是不接受，则视为确认拒绝并移除该凭证
          if (!this.feedbackForm.accepted && this.feedbackContext.originalVoucher) {
            const v = this.feedbackContext.originalVoucher
            const index = this.generatedVouchers.findIndex(x =>
              (x.id && x.id === v.id) ||
              (x.word === v.word && x.code === v.code && x.remark === v.remark)
            )
            if (index > -1) {
              this.generatedVouchers.splice(index, 1)
              this.$Message.success('已拒绝该凭证建议')
            }
          }
        } else {
          throw new Error(result && result.message ? result.message : '提交失败')
        }
      } catch (e) {
        this.$Message.error('提交反馈失败：' + e.message)
      } finally {
        this.feedbackSubmitting = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.ai-voucher-generator {
  padding: 20px;

  .subtitle {
    color: #666;
    font-size: 14px;
    margin-top: 8px;
  }

  // 期间选择器样式
  .period-selector {
    margin-bottom: 24px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    h4 {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 12px;
      color: #333;
    }

    .period-controls {
      display: flex;
      align-items: center;
      gap: 20px;
      flex-wrap: wrap;

      .period-input {
        display: flex;
        align-items: center;
        gap: 8px;

        label {
          font-size: 14px;
          font-weight: 500;
          color: #666;
          min-width: 50px;
        }

        .year-select, .month-select {
          padding: 6px 12px;
          border: 1px solid #d9d9d9;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          cursor: pointer;
          min-width: 100px;

          &:focus {
            border-color: #1890ff;
            outline: none;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
          }
        }
      }

      .period-display {
        margin-left: auto;

        .current-period {
          font-size: 14px;
          font-weight: 600;
          color: #1890ff;
          background: #f0f9ff;
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid #b3d8ff;
        }
      }
    }
  }

  .function-selector {
    margin: 24px 0;

    .selector-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #333;
    }

    .radio-cards {
      display: flex;
      gap: 16px;

      .radio-card {
        flex: 1;
        padding: 20px;
        border: 2px solid #e8e8e8;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: center;

        &:hover {
          border-color: #1890ff;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
        }

        &.active {
          border-color: #1890ff;
          background-color: #f0f9ff;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
        }

        .card-icon {
          font-size: 32px;
          margin-bottom: 12px;
        }

        .card-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #333;
        }

        .card-desc {
          font-size: 12px;
          color: #666;
          line-height: 1.4;
        }
      }
    }
  }

  .sub-mode-selector {
    margin: 20px 0;

    h4 {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 12px;
      color: #333;
    }

    .mode-tabs {
      display: flex;
      gap: 8px;

      .mode-tab {
        padding: 8px 16px;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 6px;

        &:hover {
          border-color: #1890ff;
          color: #1890ff;
        }

        &.active {
          border-color: #1890ff;
          background-color: #1890ff;
          color: white;
        }

        .tab-icon {
          font-size: 14px;
        }

        .tab-text {
          font-size: 13px;
          font-weight: 500;
        }
      }
    }
  }

  .selection-hint {
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 4px;

    p {
      margin: 0;
      font-size: 12px;
      color: #52c41a;
    }
  }

  .receipt-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    margin-bottom: 16px;

    .receipt-item {
      display: flex;
      align-items: center;
      padding: 12px;
      border-bottom: 1px solid #f0f0f0;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f9f9f9;
      }

      &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
        .receipt-checkbox { cursor: not-allowed; }
      }

      &.selected {
        background-color: #e6f7ff;
        border-color: #91d5ff;
      }

      &:last-child {
        border-bottom: none;
      }

      .receipt-info {
        flex: 1;

        .receipt-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;

          .receipt-no {
            font-weight: 600;
            color: #333;
          }

          .receipt-amount {
            font-weight: 600;
            color: #1890ff;
          }
        }

        .receipt-details {
          display: flex;
          gap: 12px;
          margin-bottom: 4px;

          span {
            font-size: 12px;
            color: #666;
          }
        }

        .receipt-summary {
          font-size: 12px;
          color: #999;
        }
      }

      .receipt-actions {
        margin-left: 12px;
      }
    }
  }

  .voucher-section {
    margin: 24px 0;

    h3 {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #333;
    }

    .section-desc {
      color: #666;
      font-size: 14px;
      margin-bottom: 20px;
      line-height: 1.5;
    }

    .ai-config {
      background: #fafafa;
      padding: 16px;
      border-radius: 6px;
      margin-bottom: 20px;

      h4 {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #333;
      }

      .config-row {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        label {
          min-width: 100px;
          font-size: 13px;
          color: #666;
        }

        .slider-container {
          position: relative;
          margin-right: 12px;

          .slider-marks {
            display: flex;
            justify-content: space-between;
            margin-top: 4px;

            .mark {
              font-size: 11px;
              color: #999;
            }
          }
        }

        .threshold-value {
          font-weight: 600;
          color: #1890ff;
          margin-right: 8px;
        }

        .threshold-desc {
          font-size: 12px;
          color: #666;
        }
      }
    }

    .data-selection {
      .bill-list, .receipt-list, .group-list, .relation-list {
        max-height: 400px;
        overflow-y: auto;
        padding: 8px;
        background: #fafafa;
        border-radius: 8px;
        margin-bottom: 16px;

        .bill-item, .receipt-item, .group-item, .relation-item {
          position: relative;

          .corner-ribbon {
            position: absolute;
            top: -6px;
            left: -6px;
            background: #52c41a;
            color: #fff;
            padding: 4px 10px;
            font-size: 12px;
            font-weight: 600;
            border-top-left-radius: 8px;
            border-bottom-right-radius: 8px;
            box-shadow: 0 2px 6px rgba(82,196,26,0.3);
          }

          .ribbon-processed {
            background: #52c41a;
          }
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          margin-bottom: 12px;
          background: white;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

          &:hover {
            border-color: #1890ff;
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
            transform: translateY(-1px);
          }

          &.disabled {
            opacity: 0.6;
            cursor: not-allowed;
            .bill-checkbox, .group-checkbox { cursor: not-allowed; }
          }

          &.selected {
            border-color: #1890ff;
            background: #f0f9ff;
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
          }

          &:last-child {
            margin-bottom: 0;
          }

          .bill-info, .relation-info {
            flex: 1;

            .bill-header, .relation-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 4px;

              .bill-no, .relation-type {
                font-weight: 600;
                color: #333;
              }

              .bill-amount, .relation-amount {
                font-weight: 600;
                color: #1890ff;
              }
            }

            .bill-details, .relation-details {
              display: flex;
              gap: 12px;
              margin-bottom: 4px;

              span {
                font-size: 12px;
                color: #666;
              }
            }

            .bill-summary, .relation-summary {
              font-size: 12px;
              color: #999;
            }
          }

          .bill-actions, .relation-actions {
            margin-left: 12px;
          }
        }
      }
    }

    .data-preview {
      background: #f9f9f9;
      padding: 16px;
      border-radius: 6px;

      h4 {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #333;
      }

      .batch-stats {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        .stat-card {
          flex: 1;
          background: white;
          padding: 16px;
          border-radius: 6px;
          text-align: center;

          h5 {
            font-size: 14px;
            margin-bottom: 8px;
            color: #333;
          }

          p {
            margin: 4px 0;
            font-size: 12px;
            color: #666;

            &:last-child {
              font-weight: 600;
              color: #1890ff;
            }
          }
        }
      }
    }

    .data-stats {
      display: flex;
      gap: 16px;
      margin-bottom: 16px;

      .stat-item {
        font-size: 13px;
        color: #666;

        &:first-child {
          font-weight: 600;
          color: #333;
        }
      }
    }
  }

  .ai-results {
    margin: 24px 0;

    h3 {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #333;
    }

    .results-summary {
      display: flex;
      gap: 24px;
      margin-bottom: 20px;
      padding: 16px;
      background: #f0f9ff;
      border-radius: 6px;
      position: relative;

      .preset-tip { position: absolute; right: 12px; top: 10px; font-size: 12px; color: #999; }
      .clear-preset { position: absolute; right: 12px; bottom: 10px; font-size: 12px; color: #1890ff; cursor: pointer; }

      .result-stat {
        font-size: 14px;
        color: #1890ff;
        font-weight: 600;
      }
    }

    .results-list {
      .result-item {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        margin-bottom: 16px;
        overflow: hidden;

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: #fafafa;
          border-bottom: 1px solid #e8e8e8;

          h4 {
            font-size: 16px;
            font-weight: 600;
            margin: 0;
            color: #333;
          }

          .confidence-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;

            &.high {
              background: #f6ffed;
              color: #52c41a;
              border: 1px solid #b7eb8f;
            }

            &.medium {
              background: #fff7e6;
              color: #fa8c16;
              border: 1px solid #ffd591;
            }

            &.low {
              background: #fff2f0;
              color: #ff4d4f;
              border: 1px solid #ffb3b3;
            }
          }
        }

        .voucher-content {
          padding: 16px;

          .voucher-info {
            margin-bottom: 16px;

            .info-row {
              display: flex;
              margin-bottom: 8px;

              .label {
                min-width: 80px;
                font-size: 13px;
                color: #666;
              }

              .value {
                font-size: 13px;
                color: #333;
                font-weight: 500;
              }
            }
          }

          .voucher-entries {
            margin-bottom: 16px;

            .voucher-header-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 12px;
              padding: 8px 12px;
              background: #f0f9ff;
              border-radius: 4px;

              .voucher-date, .voucher-number {
                font-size: 13px;
                font-weight: 600;
                color: #1890ff;
              }
            }

            h5 {
              font-size: 14px;
              font-weight: 600;
              margin-bottom: 8px;
              color: #333;
            }

            .entries-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;

              th, td {
                padding: 8px 12px;
                text-align: left;
                border: 1px solid #e8e8e8;
              }

              th {
                background: #fafafa;
                font-weight: 600;
                color: #333;
                text-align: center;
              }

              td {
                &:first-child {
                  text-align: center; // 摘要居中
                }

                &:nth-child(2) {
                  font-family: 'Courier New', monospace; // 科目编码使用等宽字体
                  font-size: 12px;
                }
              }

              .amount {
                text-align: right;
                font-family: 'Courier New', monospace;
                font-weight: 500;
              }

              .total-row {
                background: #f9f9f9;
                font-weight: 600;

                td {
                  border-top: 2px solid #1890ff;
                }
              }
            }
          }

          .ai-analysis {
            background: #f6ffed;
            padding: 12px;
            border-radius: 6px;
            border-left: 4px solid #52c41a;

            h5 {
              font-size: 13px;
              font-weight: 600;
              margin-bottom: 8px;
              color: #333;
            }

            p {
              font-size: 12px;
              color: #666;
              line-height: 1.5;
              margin: 0;
            }
          }
        }

        .result-actions {
          padding: 16px;
          background: #fafafa;
          border-top: 1px solid #e8e8e8;
          display: flex;
          gap: 8px;
        }
      }
    }

    .batch-operations {
      margin-top: 20px;
      padding: 16px;
      background: #f9f9f9;
      border-radius: 6px;
      display: flex;
      gap: 12px;
    }
  }

  // 原生复选框样式
  .checkbox-label {
    display: inline-flex;
    align-items: center;
    margin-right: 20px;
    margin-bottom: 8px;
    cursor: pointer;
    font-size: 14px;

    .config-checkbox {
      margin-right: 6px;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }

  // 复选框容器样式
  .bill-checkbox-wrapper, .receipt-checkbox-wrapper, .group-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    padding-top: 2px;
    margin-right: 4px;
  }

  .bill-checkbox, .receipt-checkbox, .group-checkbox {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
    accent-color: #1890ff;
  }

  .bill-item, .receipt-item, .group-item {
    cursor: pointer;

    &:hover {
      .bill-checkbox, .receipt-checkbox, .group-checkbox {
        transform: scale(1.1);
        transition: transform 0.2s ease;
      }
    }
  }

  // 搜索筛选区域样式
  .search-filters {
    margin-bottom: 16px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    .filter-row {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .filter-item {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;

      label {
        font-size: 13px;
        color: #666;
        white-space: nowrap;
        min-width: 60px;
      }

      input, select {
        flex: 1;
        padding: 6px 10px;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        font-size: 13px;

        &:focus {
          border-color: #1890ff;
          outline: none;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        }
      }

      .range-separator {
        color: #999;
        font-size: 12px;
      }
    }

    .filter-actions {
      display: flex;
      gap: 8px;
      margin-left: auto;

      .filter-btn {
        padding: 6px 12px;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        font-size: 13px;
        transition: all 0.2s;

        &:hover {
          border-color: #1890ff;
          color: #1890ff;
        }

        &.search-btn {
          background: #1890ff;
          color: white;
          border-color: #1890ff;

          &:hover {
            background: #40a9ff;
          }
        }

        &.reset-btn {
          &:hover {
            border-color: #ff4d4f;
            color: #ff4d4f;
          }
        }
      }
    }
  }

  // 银行回单扩展信息样式
  .receipt-extended-info {
    margin-top: 8px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #e9ecef;

    .info-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }

      .info-label {
        font-size: 11px;
        color: #666;
        font-weight: 600;
        min-width: 40px;
      }

      .info-value {
        font-size: 11px;
        color: #333;
        flex: 1;
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  // 组内项目预览样式
  .group-items-preview {
    margin-top: 12px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;

    .preview-title {
      font-size: 12px;
      font-weight: 600;
      color: #666;
      margin-bottom: 6px;
    }

    .preview-items {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .preview-item {
        font-size: 11px;
        color: #666;
        background: white;
        padding: 2px 6px;
        border-radius: 3px;
        border: 1px solid #e8e8e8;
      }

      .preview-more {
        font-size: 11px;
        color: #1890ff;
        font-weight: 600;
      }
    }
  }

  /* 新增的分页相关样式 */
  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding: 10px 0;
    border-bottom: 1px solid #e9ecef;
  }

  .receipt-header h4 {
    margin: 0;
    color: #2c3e50;
  }

  .data-summary {
    display: flex;
    gap: 20px;
    align-items: center;
  }

  .total-info {
    font-weight: 600;
    color: #007bff;
    background: #e7f3ff;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 14px;
  }

  .amount-info {
    font-weight: 600;
    color: #28a745;
    background: #e8f5e8;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 14px;
  }

  .pagination-hint {
    margin: 10px 0 0 0;
    padding: 10px;
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    color: #856404;
    font-size: 14px;
  }

  .load-more-link {
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
    font-weight: 500;
  }

  .load-more-link:hover {
    color: #0056b3;
  }

  /* 分页控制样式 */
  .pagination-controls {
    margin: 20px 0;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .pagination-info {
    font-size: 14px;
    color: #6c757d;
    margin-bottom: 10px;
    text-align: center;
  }

  .pagination-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  .pagination-btn {
    padding: 6px 12px;
    border: 1px solid #dee2e6;
    background: white;
    color: #495057;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
  }

  .pagination-btn:hover:not(:disabled) {
    background: #007bff;
    color: white;
    border-color: #007bff;
  }

  .pagination-btn:disabled {
    background: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .page-numbers {
    display: flex;
    gap: 4px;
  }

  .page-btn {
    padding: 6px 10px;
    border: 1px solid #dee2e6;
    background: white;
    color: #495057;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    min-width: 32px;
    text-align: center;
    transition: all 0.2s;
  }

  .page-btn:hover {
    background: #007bff;
    color: white;
    border-color: #007bff;
  }

  .page-btn.active {
    background: #007bff;
    color: white;
    border-color: #007bff;
    font-weight: bold;
  }

  .page-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .page-ellipsis {
    padding: 6px 10px;
    color: #6c757d;
    font-size: 14px;
    display: flex;
    align-items: center;
  }
}
</style>
<style scoped>
.feedback-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.feedback-dialog {
  width: 700px;
  max-width: 90%;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 12px 24px rgba(0,0,0,0.2);
}
.dialog-header {
  padding: 12px 16px;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}
.dialog-body {
  padding: 16px;
}
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
}
.form-row { margin-bottom: 12px; }
.input, .textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.btn { padding: 6px 12px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer; }
.btn.primary { background: #1890ff; color: #fff; border-color: #1890ff; }
.checkbox-inline { display: inline-flex; align-items: center; gap: 6px; }
</style>
